/* eslint-disable */
import type { LocalizableString } from "../cash/localization/localization.pb";
import type { Color } from "../cash/ui/Color.pb";
import type { Image as Image1 } from "../cash/ui/Image.pb";
import type { Country } from "../common/countries.pb";
import type { Money } from "../common/currency.pb";
import type { CurrencyCode } from "../common/currency_codes.pb";
import type { InstrumentType } from "../common/instrument.pb";
import type { GlobalAddress } from "../common/location.pb";
import type { ScheduledTransactionPreferenceType } from "../repeatedly/common.pb";
import type { CardPresentationStyle } from "./CardPresentationStyleProto.pb";
import type { BlockerAction } from "./blocker_action.pb";
import type { PersonaDidvBlocker } from "./blockers/PersonaDidvBlocker.pb";
import type { CheckDepositBlocker } from "./blockers/check_deposit_blocker.pb";
import type { ClabeEntryBlocker } from "./blockers/clabe_entry_blocker.pb";
import type { CreditAnimationBlocker } from "./blockers/credit_animation_blocker.pb";
import type { CreditFirstTimeBorrowBlocker } from "./blockers/credit_first_time_borrow_blocker.pb";
import type { CreditMultiStepLoadingBlocker } from "./blockers/credit_multi_step_loading_blocker.pb";
import type { InstantPayDirectDepositSwitchBlocker } from "./blockers/instant_pay_direct_deposit_switch_blocker.pb";
import type { InstrumentSelectionBlocker } from "./blockers/instrument_selection_blocker.pb";
import type { ChangeRoundUpDestinationBlocker, RoundUpOnboardingBlocker } from "./blockers/invest_automator.pb";
import type { InvestmentEntitySelectionBlocker } from "./blockers/investment_entity_selection_blocker.pb";
import type { LendingNullStateBlocker } from "./blockers/lending_null_state_blocker.pb";
import type { PaymentPlanDataBlocker } from "./blockers/lending_payment_plan_data_blocker.pb";
import type { MultiCurrencyAmountEntryBlocker } from "./blockers/multi_currency_amount_entry_blocker.pb";
import type { MultiCurrencyPaymentReviewBlocker } from "./blockers/multi_currency_payment_review_blocker.pb";
import type { OnboardingInternalRouteBlocker } from "./blockers/onboarding_internal_route_blocker.pb";
import type { OverflowOptionPickerBlocker } from "./blockers/overflow_option_picker_blocker.pb";
import type { PaperCashDepositBlocker } from "./blockers/paper_cash_deposit_blocker.pb";
import type { ParticipantSelectionBlocker } from "./blockers/participant_selection_blocker.pb";
import type { PayrollProviderSearchBlocker } from "./blockers/payroll_provider_search_blocker.pb";
import type { PinwheelLinkBlocker } from "./blockers/pinwheel_link_blocker.pb";
import type { RecipientSelectorBlocker } from "./blockers/recipient_selector_blocker.pb";
import type { RetailerMapBlocker } from "./blockers/retailer_map_blocker.pb";
import type { SetPaycheckAllocationAmountBlocker } from "./blockers/set_paycheck_allocation_amount_blocker.pb";
import type { TaxWebViewBlocker } from "./blockers/tax_web_view_blocker.pb";
import type {
  ThreeDomainSecureRedirectBlocker,
  ThreeDomainSecureV2Blocker,
} from "./blockers/three_domain_secure_redirect_blocker.pb";
import type { TreehouseBlocker } from "./blockers/treehouse_blocker.pb";
import type { TutorialBlocker } from "./blockers/tutorial_blocker.pb";
import type { WebviewBlocker } from "./blockers/webview_blocker.pb";
import type { CardCustomizationControlsTheme, CardCustomizationDataCustomizationArea, CardTheme } from "./cards.pb";
import type { CashInstrumentType } from "./cash_instrument_type.pb";
import type { FileCategory } from "./file_category.pb";
import type { FilesetUploadBlocker } from "./fileset_upload_blocker.pb";
import type {
  FormBlocker,
  FormBlockerElementAvatarElement,
  FormBlockerElementButtonElement,
  FormBlockerElementCallToActionElement,
  FormBlockerElementLocalImageElement,
  FormBlockerElementLocalImageElementIcon,
  FormBlockerElementMoneyElement,
  FormBlockerElementRemoteImageElement,
  FormBlockerElementSelectableRowElement,
  FormBlockerElementTextElement,
} from "./form_blocker.pb";
import type { RatePlan } from "./rate_plan.pb";
import type { Role } from "./role.pb";
import type { Avatar } from "./ui.pb";
import type { UiCustomer } from "./ui/customer.pb";

export enum Scope {
  /** SQUARE_CASH_SENDER - Send cash and accept requests for cash. */
  SQUARE_CASH_SENDER = "SQUARE_CASH_SENDER",
  /** SQUARE_CASH_RECIPIENT - Receive cash and create requests for cash. */
  SQUARE_CASH_RECIPIENT = "SQUARE_CASH_RECIPIENT",
}

export enum Action {
  /** SEND - The initiator is sending money. */
  SEND = "SEND",
  /** REQUEST - The initiator is requesting money. */
  REQUEST = "REQUEST",
  /** CONFIRM - Internal-only. */
  CONFIRM = "CONFIRM",
  /** RETRY - Internal-only. */
  RETRY = "RETRY",
  /** DENY - Internal-only. */
  DENY = "DENY",
  /** REFUSE - Internal-only. */
  REFUSE = "REFUSE",
  /** REJECT - Internal-only. */
  REJECT = "REJECT",
  /** REVOKE - Internal-only. */
  REVOKE = "REVOKE",
  /** RETRACT - Internal-only. */
  RETRACT = "RETRACT",
}

/**
 * IntendedUsage represents reasons that a customer selects as their primary use of Cash.
 * Each IntendedUsage maps to a RatePlan. If the RatePlan is a business rate plan, the customer
 * will need to accept the RatePlan and its fee after declaring their intended usage.
 */
export enum IntendedUsage {
  /**
   * PERSONAL_USE - The primary use for Cash is exchanging money between friends and family. Maps to a personal
   * rate plan.
   */
  PERSONAL_USE = "PERSONAL_USE",
  /** SELLING_GOODS_OR_SERVICES - The primary use for Cash is selling goods or services. Maps to a business rate plan. */
  SELLING_GOODS_OR_SERVICES = "SELLING_GOODS_OR_SERVICES",
  /** TAKING_DONATIONS - The primary use for Cash is taking donations. Maps to a business rate plan. */
  TAKING_DONATIONS = "TAKING_DONATIONS",
  /** RECEIVING_TIPS - The primary use for Cash is receiving tips. Maps to a business rate plan. */
  RECEIVING_TIPS = "RECEIVING_TIPS",
  /**
   * COLLECTING_RENT - The primary use for Cash is collecting rent (as a landlord or a roommate pooling rent to
   * send to a landlord. Maps to a personal rate plan.
   */
  COLLECTING_RENT = "COLLECTING_RENT",
  /** UNDECIDED_USE - Accounts that haven't yet selected an intended usage. */
  UNDECIDED_USE = "UNDECIDED_USE",
  /** UNDECIDED_USE_GRANDFATHERED - Accounts that were created before we started requesting intended usage. */
  UNDECIDED_USE_GRANDFATHERED = "UNDECIDED_USE_GRANDFATHERED",
  /** BUSINESS_USE - Unspecified business usage, for customers onboarded before we got more specific. */
  BUSINESS_USE = "BUSINESS_USE",
  /** NON_PROFIT_USE - Non-profit customers. */
  NON_PROFIT_USE = "NON_PROFIT_USE",
}

export enum CashtagValidity {
  CASHTAG_VALID = "CASHTAG_VALID",
  CASHTAG_INVALID_CHARACTER = "CASHTAG_INVALID_CHARACTER",
  CASHTAG_MISSING_ALPHA_CHARACTER = "CASHTAG_MISSING_ALPHA_CHARACTER",
  CASHTAG_LENGTH_BELOW_MINIMUM = "CASHTAG_LENGTH_BELOW_MINIMUM",
  CASHTAG_LENGTH_ABOVE_MAXIMUM = "CASHTAG_LENGTH_ABOVE_MAXIMUM",
  CASHTAG_APPEARS_SUSPICIOUS = "CASHTAG_APPEARS_SUSPICIOUS",
}

export enum CashtagStatus {
  /**
   * AVAILABLE - The cashtag is available (or was available and is now yours, depending on the API returning
   * this CashtagStatus).
   */
  AVAILABLE = "AVAILABLE",
  /**
   * INVALID_CASHTAG - The cashtag supplied was not valid (either its length was too short or too long, or it
   * contained an invalid character). A well behaving app should never get this status.
   */
  INVALID_CASHTAG = "INVALID_CASHTAG",
  /** UNAVAILABLE - The cashtag is not available (either it is already in use, or it is not allowed to be used). */
  UNAVAILABLE = "UNAVAILABLE",
}

/** Used to determine display order for action buttons on Invite Friends screens. */
export enum InviteFriendsBlockerButtonOrder {
  PRIMARY_BUTTON_FIRST = "PRIMARY_BUTTON_FIRST",
  SECONDARY_BUTTON_FIRST = "SECONDARY_BUTTON_FIRST",
}

export enum CardStatus {
  VALID = "VALID",
  NOT_LINKED = "NOT_LINKED",
  INVALID = "INVALID",
  EXPIRED = "EXPIRED",
  UNSUPPORTED_FOR_ROLE = "UNSUPPORTED_FOR_ROLE",
  /** UNSUPPORTED_FOR_DEPOSIT_PREFERENCE - Added for v2.12. */
  UNSUPPORTED_FOR_DEPOSIT_PREFERENCE = "UNSUPPORTED_FOR_DEPOSIT_PREFERENCE",
}

/** Returned in an ErrorResponse in response to a failed bank account link. */
export enum BankAccountErrorType {
  /** invalid_routing_number - Returned if the routing number is either invalid or unsupported. */
  invalid_routing_number = "invalid_routing_number",
  /** invalid_account_number - Returned if the account number is invalid. */
  invalid_account_number = "invalid_account_number",
}

/** Returned in an ErrorResponse in response to a failed card link. */
export enum CardErrorType {
  card_type_unsupported = "card_type_unsupported",
  invalid_card_number = "invalid_card_number",
  invalid_expiration = "invalid_expiration",
  invalid_security_code = "invalid_security_code",
  invalid_postal_code = "invalid_postal_code",
  unsupported_region = "unsupported_region",
  /** card_linked_too_many_accounts - Deprecated. It now returns invalid card number. */
  card_linked_too_many_accounts = "card_linked_too_many_accounts",
  card_blocked = "card_blocked",
  card_unsupported = "card_unsupported",
}

/** Payment buttons and related copy changes on cash.me pages depending on the type of accounts. */
export enum PaymentButtonType {
  PAY = "PAY",
  DONATE = "DONATE",
  CONTRIBUTE = "CONTRIBUTE",
}

export enum PasscodeErrorType {
  invalid_passcode = "invalid_passcode",
  too_many_attempts = "too_many_attempts",
  blocked_card = "blocked_card",
}

/** Equivalent to SortOrder in squareup/xp/v1/common.proto. */
export enum SortOrder {
  /** ASC - Results in ascending order. */
  ASC = "ASC",
  /** DESC - Results in descending order. */
  DESC = "DESC",
}

/** This error types can always be returned inside an ErrorResponse, match to HTTP status codes. */
export enum GenericErrorType {
  missing_parameter = "missing_parameter",
  invalid_parameter = "invalid_parameter",
  forbidden_parameter = "forbidden_parameter",
  conflict_parameter = "conflict_parameter",
  not_found = "not_found",
  conflict_state = "conflict_state",
  too_many_requests = "too_many_requests",
  malformed_request = "malformed_request",
  unauthorized = "unauthorized",
  service_unavailable = "service_unavailable",
}

/** A description of where in the app or web a call is originating. */
export enum ClientScenario {
  /** ONBOARDING - The call originated during onboarding of the client. */
  ONBOARDING = "ONBOARDING",
  /** LOGIN - The call originated during a sign in flow that needs to skip everything non-essential. */
  LOGIN = "LOGIN",
  /** RETURNING_CUSTOMER_LOGIN - The call originated from login flow for returning customer, that will skip 2FA and risk-evaluation. */
  RETURNING_CUSTOMER_LOGIN = "RETURNING_CUSTOMER_LOGIN",
  /** PAYMENT_FLOW - The call originated during a payment flow in the client. */
  PAYMENT_FLOW = "PAYMENT_FLOW",
  /** PROFILE - The call originated in the client's profile. */
  PROFILE = "PROFILE",
  /** ACTIVITY - The call originated in the client's activity. */
  ACTIVITY = "ACTIVITY",
  /** ENABLE_CASH_BALANCE - The call originated in a flow for enabling deposits into a customer’s stored balance. */
  ENABLE_CASH_BALANCE = "ENABLE_CASH_BALANCE",
  /**
   * DISABLE_CASH_BALANCE - The call originated in a flow for disabling deposits into a customer’s stored balance.
   * This is only supported for old apps. Use ENABLE_AUTO_CASH_OUT instead.
   */
  DISABLE_CASH_BALANCE = "DISABLE_CASH_BALANCE",
  /** ENABLE_SECURITY_LOCK - The call originated in a flow for enabling a customer’s security lock. */
  ENABLE_SECURITY_LOCK = "ENABLE_SECURITY_LOCK",
  /** DISABLE_SECURITY_LOCK - The call originated in a flow for disabling a customer’s security lock. */
  DISABLE_SECURITY_LOCK = "DISABLE_SECURITY_LOCK",
  /** CREATE_PASSCODE - The call originated in a flow for creating a customer’s passcode. */
  CREATE_PASSCODE = "CREATE_PASSCODE",
  /** CHANGE_PASSCODE - The call originated in a flow for changing a customer’s passcode. */
  CHANGE_PASSCODE = "CHANGE_PASSCODE",
  /** RESET_PASSCODE - The call originated in a flow for resetting a customer’s passcode without the current passcode. */
  RESET_PASSCODE = "RESET_PASSCODE",
  /** TRANSFER_FUNDS - The call originated in a flow to transfer funds to or from a customer's stored balance. */
  TRANSFER_FUNDS = "TRANSFER_FUNDS",
  /** GET_ISSUED_CARD - The call originated in a flow for getting a customer’s issued card. */
  GET_ISSUED_CARD = "GET_ISSUED_CARD",
  /** ACTIVATE_ISSUED_CARD - The call originated in a flow for activating a customer’s issued card. */
  ACTIVATE_ISSUED_CARD = "ACTIVATE_ISSUED_CARD",
  /** ENABLE_ISSUED_CARD - The call originated in a flow for enabling a customer’s issued card. */
  ENABLE_ISSUED_CARD = "ENABLE_ISSUED_CARD",
  /** DISABLE_ISSUED_CARD - The call originated in a flow for disabling a customer’s issued card. */
  DISABLE_ISSUED_CARD = "DISABLE_ISSUED_CARD",
  /** LOCK_ISSUED_CARD - The call originated in a flow for locking a customer’s issued card. */
  LOCK_ISSUED_CARD = "LOCK_ISSUED_CARD",
  /** UNLOCK_ISSUED_CARD - The call originated in a flow for unlocking a customer’s issued card. */
  UNLOCK_ISSUED_CARD = "UNLOCK_ISSUED_CARD",
  /**
   * ENABLE_AUTO_CASH_OUT - The call originated in a flow for enabling auto cash outs. The deposit preference to set may
   * still be undecided at this point. This is equivalent of DISABLE_CASH_BALANCE for apps prior
   * to v2.12.
   */
  ENABLE_AUTO_CASH_OUT = "ENABLE_AUTO_CASH_OUT",
  /** ENABLE_TRANSFER_INSTANTLY_WITH_FEE - The call originated in a flow for enabling transfer instantly with fee deposit preference. */
  ENABLE_TRANSFER_INSTANTLY_WITH_FEE = "ENABLE_TRANSFER_INSTANTLY_WITH_FEE",
  /** ENABLE_TRANSFER_SLOWLY_WITHOUT_FEE - The call originated in a flow for enabling transfer slow without fee deposit preference. */
  ENABLE_TRANSFER_SLOWLY_WITHOUT_FEE = "ENABLE_TRANSFER_SLOWLY_WITHOUT_FEE",
  /**
   * DEPOSIT_INSTANTLY_FROM_SLOW - The call originated in a flow to deposit payment instantly where
   * the payment was originally going to be deposited slowly.
   */
  DEPOSIT_INSTANTLY_FROM_SLOW = "DEPOSIT_INSTANTLY_FROM_SLOW",
  /** PROVISION_APPLE_PAY - The call originates in a flow for provisioning an issued card for Apple Pay. */
  PROVISION_APPLE_PAY = "PROVISION_APPLE_PAY",
  /**
   * SUGGEST_MERGE - The call originated in a flow to optionally merge into a similar customer prompted
   * by the server.
   */
  SUGGEST_MERGE = "SUGGEST_MERGE",
  /** REQUEST_PHYSICAL_CARD - The call originated in a flow for requesting a physical debit card. */
  REQUEST_PHYSICAL_CARD = "REQUEST_PHYSICAL_CARD",
  /**
   * REQUEST_PHYSICAL_CARD_APP_MESSAGE - The call originated in a flow for requesting a physical card from an app message.
   * This scenario can have different blockers than REQUEST_PHYSICAL_CARD.
   */
  REQUEST_PHYSICAL_CARD_APP_MESSAGE = "REQUEST_PHYSICAL_CARD_APP_MESSAGE",
  /** ACTIVATE_PHYSICAL_CARD - The call originated in a flow for activating a physical debit card after it has arrived. */
  ACTIVATE_PHYSICAL_CARD = "ACTIVATE_PHYSICAL_CARD",
  /** ACQUIRE_ALIAS - The call originated in a flow for acquiring an alias when already logged in. */
  ACQUIRE_ALIAS = "ACQUIRE_ALIAS",
  /** ACTIVATE_DIRECT_DEPOSIT_ACCOUNT - The call originated in a flow for activating a direct deposit account. */
  ACTIVATE_DIRECT_DEPOSIT_ACCOUNT = "ACTIVATE_DIRECT_DEPOSIT_ACCOUNT",
  /** REPORT_LOST_CARD - The call originated in a flow to report the loss/theft of a physical card. */
  REPORT_LOST_CARD = "REPORT_LOST_CARD",
  /**
   * REPORT_MISSING_REWARD - The call originated in a flow to report a missing reward.
   *
   * @deprecated
   */
  REPORT_MISSING_REWARD = "REPORT_MISSING_REWARD",
  /** ENABLE_CRYPTOCURRENCY - The call originated in a flow to enable a BTC balance. */
  ENABLE_CRYPTOCURRENCY = "ENABLE_CRYPTOCURRENCY",
  /** TRANSFER_CRYPTOCURRENCY - The call originated in a flow to transfer BTC from BTC balance. */
  TRANSFER_CRYPTOCURRENCY = "TRANSFER_CRYPTOCURRENCY",
  /** EXCHANGE_CURRENCY - The call originated in a flow to exchange currency (e.g., BTC for USD, or USD for BTC). */
  EXCHANGE_CURRENCY = "EXCHANGE_CURRENCY",
  /** ENABLE_CRYPTOCURRENCY_TRANSFER_IN - The call originated in a flow to enable transfers from an external wallet to the BTC balance. */
  ENABLE_CRYPTOCURRENCY_TRANSFER_IN = "ENABLE_CRYPTOCURRENCY_TRANSFER_IN",
  /** ENABLE_CRYPTOCURRENCY_TRANSFER_OUT - The call originated in a flow to enable transfers from the BTC balance to an external wallet. */
  ENABLE_CRYPTOCURRENCY_TRANSFER_OUT = "ENABLE_CRYPTOCURRENCY_TRANSFER_OUT",
  /** GET_REWARDS - The call originated in a flow to get rewards. */
  GET_REWARDS = "GET_REWARDS",
  /** ADD_OR_UPDATE_REWARD - The call originated in a flow to add or update a reward. */
  ADD_OR_UPDATE_REWARD = "ADD_OR_UPDATE_REWARD",
  /** REPORT_MISSING_BOOST - The call originated in a flow to report a missing boost on a transaction. */
  REPORT_MISSING_BOOST = "REPORT_MISSING_BOOST",
  /** ENABLE_SCHEDULED_RELOAD - A flow to create or update a scheduled reload preference (aka auto add cash). */
  ENABLE_SCHEDULED_RELOAD = "ENABLE_SCHEDULED_RELOAD",
  /** DISABLE_SCHEDULED_RELOAD - A flow to disable the scheduled reload preference. */
  DISABLE_SCHEDULED_RELOAD = "DISABLE_SCHEDULED_RELOAD",
  /** UPDATE_SCHEDULED_RELOAD_AMOUNT - A flow to update the scheduled reload amount. */
  UPDATE_SCHEDULED_RELOAD_AMOUNT = "UPDATE_SCHEDULED_RELOAD_AMOUNT",
  /** UPDATE_SCHEDULED_RELOAD_FREQUENCY - A flow to update the scheduled reload recurring schedule. */
  UPDATE_SCHEDULED_RELOAD_FREQUENCY = "UPDATE_SCHEDULED_RELOAD_FREQUENCY",
  /** EXCHANGE_EQUITY - A flow to trade equities for cash. */
  EXCHANGE_EQUITY = "EXCHANGE_EQUITY",
  /** ACQUIRE_ALIAS_FOR_MISSING_PAYMENT - A flow to find a missing payment. */
  ACQUIRE_ALIAS_FOR_MISSING_PAYMENT = "ACQUIRE_ALIAS_FOR_MISSING_PAYMENT",
  /** PROVISION_GOOGLE_PAY - The call originates in a flow for provisioning an issued card for Google Pay. */
  PROVISION_GOOGLE_PAY = "PROVISION_GOOGLE_PAY",
  /** CHANGE_CARD_DESIGN - A flow to customize and order a physical card again. */
  CHANGE_CARD_DESIGN = "CHANGE_CARD_DESIGN",
  /** RESOLVE_SUSPENSION - A flow to provide information needed to unsuspend a customer. */
  RESOLVE_SUSPENSION = "RESOLVE_SUSPENSION",
  /** REQUEST_EMERGENCY_CARD - A flow to request a temporary emergency card. */
  REQUEST_EMERGENCY_CARD = "REQUEST_EMERGENCY_CARD",
  /** VERIFY_INSTRUMENT - A flow to verify a risky instrument. */
  VERIFY_INSTRUMENT = "VERIFY_INSTRUMENT",
  /** BITCOIN_DEPOSIT_REVERSAL - A flow for reversing a rejected Bitcoin deposit to an external wallet address. */
  BITCOIN_DEPOSIT_REVERSAL = "BITCOIN_DEPOSIT_REVERSAL",
  /** LINK_BANK_ACCOUNT_USING_YODLEE - A flow to request link a new bank account using Yodlee. */
  LINK_BANK_ACCOUNT_USING_YODLEE = "LINK_BANK_ACCOUNT_USING_YODLEE",
  /** RECOVER_ACCOUNT - Identify an account to recover and then verify sufficient assets to be allowed back in. */
  RECOVER_ACCOUNT = "RECOVER_ACCOUNT",
  /** ADD_EMAIL_ALIAS - A flow to add an email alias to the customer account. */
  ADD_EMAIL_ALIAS = "ADD_EMAIL_ALIAS",
  /** CONFIRM_CLOSE_ACCOUNT - This client scenario will directly close the customer's account, if they are eligible. */
  CONFIRM_CLOSE_ACCOUNT = "CONFIRM_CLOSE_ACCOUNT",
  /**
   * CLOSE_ACCOUNT - This is a placeholder for a client scenario that will be implemented. It will encapsulate the flow for
   * ensuring a customer is eligible to close their account, and if the criteria are met, will close the account.
   */
  CLOSE_ACCOUNT = "CLOSE_ACCOUNT",
  /** ADD_SMS_ALIAS - A flow to add a phone number alias to the customer account. */
  ADD_SMS_ALIAS = "ADD_SMS_ALIAS",
  /** INITIATE_LOAN - A flow to initiate a borrow instantly loan. */
  INITIATE_LOAN = "INITIATE_LOAN",
  /** INITIATE_LOAN_PAYMENT - A flow to initiate a loan payment. */
  INITIATE_LOAN_PAYMENT = "INITIATE_LOAN_PAYMENT",
  APPROVE_ACCOUNT_INTEGRATION = "APPROVE_ACCOUNT_INTEGRATION",
  /** RECOVER_ALIAS_ACCOUNT - Recover Account implicit flavour. */
  RECOVER_ALIAS_ACCOUNT = "RECOVER_ALIAS_ACCOUNT",
  INVITE_FRIENDS = "INVITE_FRIENDS",
  /** SKIP_LOAN_PAYMENT - A flow to skip a loan payment. */
  SKIP_LOAN_PAYMENT = "SKIP_LOAN_PAYMENT",
  /** ENABLE_SCHEDULED_INVESTMENT_BUY - A flow to create or update a scheduled investment (equity/btc) buy preference. */
  ENABLE_SCHEDULED_INVESTMENT_BUY = "ENABLE_SCHEDULED_INVESTMENT_BUY",
  /** DISABLE_SCHEDULED_INVESTMENT_BUY - A flow to disable the a scheduled (equity/btc) buy preference. */
  DISABLE_SCHEDULED_INVESTMENT_BUY = "DISABLE_SCHEDULED_INVESTMENT_BUY",
  /** UPDATE_SCHEDULED_INVESTMENT_BUY_AMOUNT - A flow to update the a scheduled (equity/btc) buy preference. */
  UPDATE_SCHEDULED_INVESTMENT_BUY_AMOUNT = "UPDATE_SCHEDULED_INVESTMENT_BUY_AMOUNT",
  /** UPDATE_SCHEDULED_INVESTMENT_BUY_FREQUENCY - A flow to update the a scheduled (equity/btc) buy preference. */
  UPDATE_SCHEDULED_INVESTMENT_BUY_FREQUENCY = "UPDATE_SCHEDULED_INVESTMENT_BUY_FREQUENCY",
  /** SEND_TAX_FORM_EMAIL - A flow to send investing tax form to customer's email. */
  SEND_TAX_FORM_EMAIL = "SEND_TAX_FORM_EMAIL",
  /** AMEND_CARD_MAILING_ADDRESS - Update the shipping address for a new physical card. */
  AMEND_CARD_MAILING_ADDRESS = "AMEND_CARD_MAILING_ADDRESS",
  /** INITIATE_REFUND_REQUEST - Initiate payment sender request for a refund on a payment. */
  INITIATE_REFUND_REQUEST = "INITIATE_REFUND_REQUEST",
  /** REVIEW_REFUND_REQUEST - Payment recipient review of a senders request for a refund (Refund or decline). */
  REVIEW_REFUND_REQUEST = "REVIEW_REFUND_REQUEST",
  /** OAUTH - A flow to complete authorization of a third party. */
  OAUTH = "OAUTH",
  /** DEPOSIT_CHECK - A flow to deposit a check. */
  DEPOSIT_CHECK = "DEPOSIT_CHECK",
  /** DDA_DETAILS - A flow to show specifics around a customer's DDA and how to setup direct deposit. */
  DDA_DETAILS = "DDA_DETAILS",
  /**
   * GOVERNMENT_STIMULUS_1 - Client scenarios reserved potentially new flows that we introduce as a part of handling
   * government stimulus payouts for COVID-19. (May or may not be used.)
   */
  GOVERNMENT_STIMULUS_1 = "GOVERNMENT_STIMULUS_1",
  GOVERNMENT_STIMULUS_2 = "GOVERNMENT_STIMULUS_2",
  GOVERNMENT_STIMULUS_3 = "GOVERNMENT_STIMULUS_3",
  GOVERNMENT_STIMULUS_4 = "GOVERNMENT_STIMULUS_4",
  GOVERNMENT_STIMULUS_5 = "GOVERNMENT_STIMULUS_5",
  DISPUTE_CARD_TRANSACTION = "DISPUTE_CARD_TRANSACTION",
  /** ENABLE_LOAN_AUTOPAY - A flow to enable autopay for a loan. */
  ENABLE_LOAN_AUTOPAY = "ENABLE_LOAN_AUTOPAY",
  /** DISABLE_LOAN_AUTOPAY - A flow to disable autopay for a loan. */
  DISABLE_LOAN_AUTOPAY = "DISABLE_LOAN_AUTOPAY",
  /** INITIATE_CRYPTO_PAYMENT - Initiating a request to pay someone in crypto */
  INITIATE_CRYPTO_PAYMENT = "INITIATE_CRYPTO_PAYMENT",
  /** GET_DIRECT_DEPOSIT_FORM - A flow to create a form to setup direct deposits. */
  GET_DIRECT_DEPOSIT_FORM = "GET_DIRECT_DEPOSIT_FORM",
  /** VERIFY_IDENTITY - A flow to trigger IDV. */
  VERIFY_IDENTITY = "VERIFY_IDENTITY",
  /** VIEW_DIRECT_DEPOSIT_FORM - Initiate a request to get a previously generated direct deposit form. */
  VIEW_DIRECT_DEPOSIT_FORM = "VIEW_DIRECT_DEPOSIT_FORM",
  /**
   * UNLOCK_BORROW - A flow to unlock Borrow for customers that haven't completed the requirements.
   *
   * Deprecated: This has been dropped in favor of Borrow Null State.
   *
   * @deprecated
   */
  UNLOCK_BORROW = "UNLOCK_BORROW",
  /** REVIEW_BORROW_FEE_STATUS - A flow to review the customer's fee status for Borrow. */
  REVIEW_BORROW_FEE_STATUS = "REVIEW_BORROW_FEE_STATUS",
  /** GOOGLE_PAY_PROVISIONING - A flow to assist google pay when provisioning a physical card. */
  GOOGLE_PAY_PROVISIONING = "GOOGLE_PAY_PROVISIONING",
  /** GET_EVENT_BASED_BOOST_DETAIL - A flow to retrieve details on event based boost. */
  GET_EVENT_BASED_BOOST_DETAIL = "GET_EVENT_BASED_BOOST_DETAIL",
  /**
   * INITIATE_BANK_ACCOUNT_LINKING - A flow to link a bank account via OAuth or manual ACH.
   *
   * Unused as of 3.27, reserved for future use by the Network workstream.
   */
  INITIATE_BANK_ACCOUNT_LINKING = "INITIATE_BANK_ACCOUNT_LINKING",
  /** REPORT_SCAM - A flow to report a suspected scam payment. */
  REPORT_SCAM = "REPORT_SCAM",
  /** ACCEPT_CRYPTO_PAYMENT - Accept a pending crypto payment */
  ACCEPT_CRYPTO_PAYMENT = "ACCEPT_CRYPTO_PAYMENT",
  /** UNLOCK_QR_CODE_BOOST - Unlock boost using QR code */
  UNLOCK_QR_CODE_BOOST = "UNLOCK_QR_CODE_BOOST",
  /** DEEP_LINK_ACTIVATE_PHYSICAL_CARD - Deep link activate physical card */
  DEEP_LINK_ACTIVATE_PHYSICAL_CARD = "DEEP_LINK_ACTIVATE_PHYSICAL_CARD",
  /** ACCEPT_INVEST_PAYMENT - Recipient accepts p2p stock gift */
  ACCEPT_INVEST_PAYMENT = "ACCEPT_INVEST_PAYMENT",
  /** SEND_INVEST_PAYMENT - Sender sends p2p stock gift */
  SEND_INVEST_PAYMENT = "SEND_INVEST_PAYMENT",
  /** EDIT_PROFILE_BIO - A flow to edit your bio. */
  EDIT_PROFILE_BIO = "EDIT_PROFILE_BIO",
  /** REPORT_PROFILE - A flow to report a profile. */
  REPORT_PROFILE = "REPORT_PROFILE",
  /** DECLINE_CRYPTO_PAYMENT - Decline a pending crypto payment */
  DECLINE_CRYPTO_PAYMENT = "DECLINE_CRYPTO_PAYMENT",
  /** UPGRADE_APP_VERSION_LENDING - A flow that forces a customer to update their app to access Lending functionality. */
  UPGRADE_APP_VERSION_LENDING = "UPGRADE_APP_VERSION_LENDING",
  /**
   * INITIATE_LENDING_FLOW_2 - A flow used in the Lending Repayment Optimization Experiments to display rate change
   * information to users in some of the buckets.
   */
  INITIATE_LENDING_FLOW_2 = "INITIATE_LENDING_FLOW_2",
  /**
   * INITIATE_LENDING_FLOW_1 - ClientScenarios for future Lending use, such as displaying a message of upcoming offer
   * expiration, or informing a customer of changes to their credit line.
   */
  INITIATE_LENDING_FLOW_1 = "INITIATE_LENDING_FLOW_1",
  INITIATE_LENDING_FLOW_3 = "INITIATE_LENDING_FLOW_3",
  INITIATE_LENDING_FLOW_4 = "INITIATE_LENDING_FLOW_4",
  INITIATE_LENDING_FLOW_5 = "INITIATE_LENDING_FLOW_5",
  INITIATE_LENDING_FLOW_6 = "INITIATE_LENDING_FLOW_6",
  INITIATE_LENDING_FLOW_7 = "INITIATE_LENDING_FLOW_7",
  NEW_DEVICE_LOGIN = "NEW_DEVICE_LOGIN",
  ACTIVATE_PHYSICAL_CARD_FROM_NOTIFICATION = "ACTIVATE_PHYSICAL_CARD_FROM_NOTIFICATION",
  DEPOSIT_PAPER_CASH = "DEPOSIT_PAPER_CASH",
  GET_BANKING_INTERSTITIAL = "GET_BANKING_INTERSTITIAL",
  TRANSFER_FROM_BANKS = "TRANSFER_FROM_BANKS",
  /** REQUEST_SPONSORSHIP - A flow for an under-18 account to request sponsorship from an adult. */
  REQUEST_SPONSORSHIP = "REQUEST_SPONSORSHIP",
  /** APPROVE_SPONSORSHIP - A flow for an adult to approve a sponsorship request from an under-18 account. */
  APPROVE_SPONSORSHIP = "APPROVE_SPONSORSHIP",
  /** INITIATE_TAX_UPGRADE - A flow for ensuring a customer meets IRS requirements to file taxes with us. */
  INITIATE_TAX_UPGRADE = "INITIATE_TAX_UPGRADE",
  DEPOSIT_PAPER_CASH_CONFIRMATION = "DEPOSIT_PAPER_CASH_CONFIRMATION",
  GET_INSTANT_PAYOUT = "GET_INSTANT_PAYOUT",
  /** UNLOCK_BANKING_DEPOSIT_OPTIONS - A flow that unlocks banking options for the user. */
  UNLOCK_BANKING_DEPOSIT_OPTIONS = "UNLOCK_BANKING_DEPOSIT_OPTIONS",
  /** INITIATE_CRYPTO_WITHDRAWAL - A flow to intitiate a BTC withdrawal to the blockchain */
  INITIATE_CRYPTO_WITHDRAWAL = "INITIATE_CRYPTO_WITHDRAWAL",
  /** ENTER_QR_MARKETING_EVENT - A flow to enter a marketing event by scanning a QR code. */
  ENTER_QR_MARKETING_EVENT = "ENTER_QR_MARKETING_EVENT",
  /** CARD_EXPIRATION - A flow to notify customer their card is expiring and to reorder card. */
  CARD_EXPIRATION = "CARD_EXPIRATION",
  /**
   * PLASMA - A dummy ClientScenario that denotes that the flow was handled by Plasma.
   *
   * The logic involved in this case does not use the concept of ClientScenarios and so a true one is
   * not returned.
   */
  PLASMA = "PLASMA",
  /** ACCEPT_TERMS_OF_SERVICE_AGREEMENT - A flow to display and allow user to accept terms of service agreement */
  ACCEPT_TERMS_OF_SERVICE_AGREEMENT = "ACCEPT_TERMS_OF_SERVICE_AGREEMENT",
  /** ADD_CASH - A flow related to pressing the Add Cash button. Currently used for Debit Cash Ins. */
  ADD_CASH = "ADD_CASH",
  /**
   * STANDALONE_IDV - A flow to trigger IDV for customers directly without them having to buy stocks/send btc/etc.
   *
   * This is different from the VERIFY_IDENTITY flow because VERIFY_IDENTITY is actually intended
   * for customers who have already successfully IDVed and are trying to re-do IDV to update their
   * information (for example, a legal name change). This flow is intended for customers who were
   * previously unable to complete IDV and are trying to continue IDV from a link we sent them in
   * a notification/email.
   */
  STANDALONE_IDV = "STANDALONE_IDV",
  /** DIRECT_DEPOSIT_SWITCH_VIA_PAYROLL - A flow to switch customer direct deposit via payroll login. */
  DIRECT_DEPOSIT_SWITCH_VIA_PAYROLL = "DIRECT_DEPOSIT_SWITCH_VIA_PAYROLL",
  /** CARD_LSB_REORDER - A flow to notify customer their shazam card is about to terminate and to reorder card. */
  CARD_LSB_REORDER = "CARD_LSB_REORDER",
  /** RESEND_SPONSORSHIP_REQUEST - A flow to trigger an additional request for sponsorship for an under-18 account. */
  RESEND_SPONSORSHIP_REQUEST = "RESEND_SPONSORSHIP_REQUEST",
  /** INITIATE_LIGHTNING_WITHDRAWAL - A flow to initiate a withdrawal of BTC via the lightning network. */
  INITIATE_LIGHTNING_WITHDRAWAL = "INITIATE_LIGHTNING_WITHDRAWAL",
  /** LINK_PAYROLL_ACCOUNT - A flow to provide payroll login primer and authenticate a customer with its payroll provider. */
  LINK_PAYROLL_ACCOUNT = "LINK_PAYROLL_ACCOUNT",
  /** GET_ESCHEATMENTS - A flow to launch (hackweek) government escheatments flow. */
  GET_ESCHEATMENTS = "GET_ESCHEATMENTS",
  /** SEND_PRINT_CHECK - A flow to launch (hackweek) send or print check flow. */
  SEND_PRINT_CHECK = "SEND_PRINT_CHECK",
  /**
   * RE_VERIFY_IDV - This flow is for customers who were verified incorrectly. It will first invalidate all
   * previous attempts for the customer and send them through the IDV subflow to verify again.
   */
  RE_VERIFY_IDV = "RE_VERIFY_IDV",
  /** DIRECT_DEPOSIT_SWITCH_VIA_PAYROLL_CONFIRMATION - Flow to confirm switching customer's direct deposit via payroll. */
  DIRECT_DEPOSIT_SWITCH_VIA_PAYROLL_CONFIRMATION = "DIRECT_DEPOSIT_SWITCH_VIA_PAYROLL_CONFIRMATION",
  /** DD_SWITCH_UPSELL_CARD_ACTIVATED - Flow for upsell direct deposit switch at the end of a successful card activation. */
  DD_SWITCH_UPSELL_CARD_ACTIVATED = "DD_SWITCH_UPSELL_CARD_ACTIVATED",
  /** ACCEPT_GIFT_CARD_PAYMENT - Recipient accepts p2p gift card */
  ACCEPT_GIFT_CARD_PAYMENT = "ACCEPT_GIFT_CARD_PAYMENT",
  /** CANCEL_INVEST_PAYMENT - Recipient cancels p2p stock gift */
  CANCEL_INVEST_PAYMENT = "CANCEL_INVEST_PAYMENT",
  /** INITIATE_BORROW_NULL_STATE_FLOW - Flow to show Borrow to ineligible customers */
  INITIATE_BORROW_NULL_STATE_FLOW = "INITIATE_BORROW_NULL_STATE_FLOW",
  /** INITIATE_GLOBAL_PAYMENT - Initiate a payment to a customer on another payment network (Global Network of Networks) */
  INITIATE_GLOBAL_PAYMENT = "INITIATE_GLOBAL_PAYMENT",
  /** CONVERT_GIFT_CARD_TO_CASH - Convert a gift card payment to cash in user's account */
  CONVERT_GIFT_CARD_TO_CASH = "CONVERT_GIFT_CARD_TO_CASH",
  /** DIRECT_DEPOSIT_SWITCH_UPSELL - Upsell direct deposit switch. To be used in different contexts: card ordering, activation, etc. */
  DIRECT_DEPOSIT_SWITCH_UPSELL = "DIRECT_DEPOSIT_SWITCH_UPSELL",
  /** FAMILY_ACCOUNT - Family account menu from the app. */
  FAMILY_ACCOUNT = "FAMILY_ACCOUNT",
  /** CANCEL_PENDING_ROUND_UP_EXECUTION - Cancel a pending Round Up execution. */
  CANCEL_PENDING_ROUND_UP_EXECUTION = "CANCEL_PENDING_ROUND_UP_EXECUTION",
  /** SEND_GIFT_CARD_PAYMENT - Send a Gift Card payment to other customers. */
  SEND_GIFT_CARD_PAYMENT = "SEND_GIFT_CARD_PAYMENT",
  /** ACTIVATE_PHYSICAL_CARD_IN_POSTCARD - New activation client scenario to route activation to a new location */
  ACTIVATE_PHYSICAL_CARD_IN_POSTCARD = "ACTIVATE_PHYSICAL_CARD_IN_POSTCARD",
  /**
   * INITIATE_BITCOIN_WITHDRAWAL - A flow to initiate a withdrawal of BTC via the lightning network or on-chain.
   * This is a new unified flow to replace INITIATE_CRYPTO_WITHDRAWAL and  INITIATE_LIGHTNING_WITHDRAWAL.
   */
  INITIATE_BITCOIN_WITHDRAWAL = "INITIATE_BITCOIN_WITHDRAWAL",
  GET_NON_RECOURSE_PAYOUT = "GET_NON_RECOURSE_PAYOUT",
  /** WIRE_TRANSFERS - This flow will be kicked off to display details about how to make a Wire transfer. */
  WIRE_TRANSFERS = "WIRE_TRANSFERS",
  /**
   * ENABLE_APP_LOCK - A flow to enable the app lock feature.
   * After a timeout period customers would need to authenticate via biometrics or passcode to use the app.
   */
  ENABLE_APP_LOCK = "ENABLE_APP_LOCK",
  /** DISABLE_APP_LOCK - A flow to disable the app lock feature. */
  DISABLE_APP_LOCK = "DISABLE_APP_LOCK",
  /**
   * UNLOCK_APP - A flow to prompt the user to authenticate when the app lock feature is enabled and the
   * app has been inactive longer than the timeout threshold allows.
   */
  UNLOCK_APP = "UNLOCK_APP",
  /** BANK_AND_WIRE_TRANSFERS - This flow will be kicked off if a user has not gone through idv but should get access to Wires. */
  BANK_AND_WIRE_TRANSFERS = "BANK_AND_WIRE_TRANSFERS",
  /** FIND_MONEY_RETAILERS - A flow to show retailers where money can be withdrawn */
  FIND_MONEY_RETAILERS = "FIND_MONEY_RETAILERS",
  /** IDENTITY_HUB - A flow to manage IDV info */
  IDENTITY_HUB = "IDENTITY_HUB",
  /** RESEND_SPONSORSHIP_FEATURE_INVITATION - A flow to trigger an additional request for out of network sponsorship for an under-18 account. */
  RESEND_SPONSORSHIP_FEATURE_INVITATION = "RESEND_SPONSORSHIP_FEATURE_INVITATION",
  /** INITIATE_MULTICURRENCY_PAYMENT_FOR_RECIPIENT - A flow which kicks off the crossborder-2.0 payment experience, used when the recipient is known */
  INITIATE_MULTICURRENCY_PAYMENT_FOR_RECIPIENT = "INITIATE_MULTICURRENCY_PAYMENT_FOR_RECIPIENT",
  /**
   * INITIATE_MULTICURRENCY_PAYMENT_FOR_REGION - A flow which kicks off the crossborder-2.0 payment experience, used when the recipient is not
   * known, but their region is known
   */
  INITIATE_MULTICURRENCY_PAYMENT_FOR_REGION = "INITIATE_MULTICURRENCY_PAYMENT_FOR_REGION",
}

/** ClientPolicies used by TPPs, configured in PlatformClientsModule. */
export enum PlatformClientPolicy {
  TPP_PAY_WITH_CASH = "TPP_PAY_WITH_CASH",
  TPP_P2P = "TPP_P2P",
  TPP_READONLY = "TPP_READONLY",
  TPP_DISPURSEMENTS = "TPP_DISPURSEMENTS",
}

export interface AuthorizeRequest {
  /**
   * An opaque value used by the client to maintain state between request and callback.
   *
   * At most 255 characters.
   */
  state?: string;
  /**
   * Defaults to “code”, which adds a code request parameter to the redirect
   * URI. If “token”, adds access token to the redirect URI fragment.
   *
   * Only "code" will be supported for API launch because of security complexity
   * around allowing end-users to take actions as an application.
   */
  response_type?: string;
  /**
   * The ID of the client (i.e. application) making the request.
   *
   * Internally this is the TPP ID.
   */
  client_id?: string;
  /**
   * 302 target. If not specified, redirect_uri from client's configuration will
   * be used.
   *
   * At most 1024 characters.
   */
  redirect_uri?: string;
  /**
   * A client-specified unique ID. Cash customer IDs may contain alphanumerics,
   * dashes, and underscores, and may be up to 64 characters. If null an ID will
   * be generated.
   */
  customer_id?: string;
  /**
   * The customer’s email address to pre-fill the enrollment form with. The
   * email address included in this request is not necessarily the address that
   * will be linked: the customer may be given an opportunity to select another
   * email address.
   *
   * At most 150 characters.
   */
  email?: string;
  /**
   * A space-separated list of the permissions the application is requesting.
   * Valid permissions are SQUARE_CASH_SENDER and SQUARE_CASH_RECIPIENT.
   * Default is both: "SQUARE_CASH_SENDER SQUARE_CASH_RECIPIENT".
   */
  scope?: string;
  /**
   * A valid grant_id issued by cash-grantly that can be used to look
   * up and authorize an existing customer.
   */
  grant_id?: string;
}

export interface AuthorizeResponse {
  /** State value from request if provided. */
  state?: string;
  /** Type of error. Non-null if this response has an error. */
  error?: string;
  /** Reason error occurred. Non-null if this response has an error. */
  error_description?: string;
  /**
   * Code exchangeable for access token at https://connect.squareup.com/oauth2/token
   * Only used when response_type is "code".
   */
  code?: string;
  /** Access token for the customer. Only used when response_type is "token". */
  access_token?: string;
  /** Authorized customer ID. */
  customer_id?: string;
  /** Will be “bearer”. Only used when response_type is "token". */
  token_type?: string;
  /**
   * ISO 8601 datetime when the access token will expire. Only used when
   * response_type is "token".
   */
  expires_at?: string;
  /**
   * URL to complete customer approval, or null if the customer's approval
   * is complete. Visiting this URL doesn't require additional authentication,
   * and so it should only be shared with the customer who is authorized. This
   * URL may only be valid for a limited time.
   */
  url?: string;
  /**
   * URL to complete customer approval using an enrollment flow, or null if the customer's approval
   * is complete. Visiting this URL doesn't require additional authentication,
   * and so it should only be shared with the customer who is authorized. This
   * URL may only be valid for a limited time.
   *
   * This attribute only populated for trusted clients.
   */
  enroll_url?: string;
}

/**
 * Addresses the sender or recipient of a payment. Callers should set one field
 * of the CashCustomer that they wish to address, and Cash will find that
 * customer, inviting her if necessary.
 *
 * When addressing a customer, avoid setting multiple fields because the result
 * could be ambiguous.
 *
 * Public API.
 */
export interface CashCustomer {
  /**
   * The customer ID.
   *
   * Internally this is a TPP's customer alias. IDs cannot be shared across TPPs.
   */
  id?: string;
  /** Email address. */
  email?: string;
  /** Phone number. */
  phone_number?: string;
  /** The customer's full name if known and available. */
  full_name?: string;
  /** Cashtag, without a leading "$". */
  cashtag?: string;
  /** Internal-only. */
  profile_photo_400_400_url?: string;
  /** URL to fetch the customer's profile photo. */
  profile_photo_url?: string;
  /** If true, this customer is a business (Cash pro) customer. */
  is_business?: boolean;
  /** The alpha-2 code for the customer's country */
  country_code?: Country;
}

export interface CreatePaymentRequest {
  /**
   * Deprecated. Replaced with id.
   *
   * @deprecated
   */
  payment_id?: string;
  /**
   * A client-specified unique ID. Provides idempotence if the request is
   * retried. Cash payment IDs may contain alphanumerics, dashes, and
   * underscores, and may be up to 64 characters.
   */
  id?: string;
  /** Either SEND or REQUEST. */
  action?: Action;
  /**
   * Who this REQUEST action is requesting cash from. This is unused for SEND
   * actions, which uses the API caller as the sender.
   */
  sender?: CashCustomer;
  /**
   * Who this SEND action is sending cash to. This is unused for REQUEST
   * actions, which uses the API caller as the recipient.
   */
  recipient?: CashCustomer;
  /** The amount of money requested in the payment. Must be positive. */
  amount_money?: Money;
  /** Optional. Customization for the sender. */
  sender_customization?: Customization;
  /** Optional. Customization for the recipient. */
  recipient_customization?: Customization;
  /** Internal-only */
  target_payout_received_at?: string;
  /**
   * Server signature to prove that this payment was authorized. It's an HMAC-SHA256 signature of
   * a JSON list (without spaces) containing [sender, recipient, payment_id, amount] in this
   * order. The server must use the client_secret as key for the HMAC.
   *
   * Internal/Snapchat only.
   */
  signature?: string;
  /**
   * Proof of ownership of the linked card.
   *
   * Internal/Snapchat only.
   */
  card_token?: string;
  /**
   * Optional. A word (or a few words) describing what this payment is for. This
   * is intended to be displayed within a sentence: for example a note of "rent"
   * might yield "$500 for rent.". Typically this is lowercase, though proper
   * nouns should be title case like "Beyoncé tickets".
   *
   * At most 50 characters.
   */
  note?: string;
  /**
   * Optional. The creation mechanism to use for this payment. Must be a valid creation mechanism
   * for the client, as specified in its client configuration.
   *
   * Internal only.
   */
  creation_mechanism?: string;
  /** The instrument selection which includes the accepted fee. */
  instrument_selection?: InstrumentSelection;
  /**
   * A client-specified external non-unique data to associate to this payment.
   *  The data may contain alphanumerics, dashes, and underscores, and may be up to 64 characters.
   *  NOTE: This data is not shown to Cash App customers.
   */
  custom?: string;
}

export interface Customization {
  /**
   * Deprecated: Email subject customization is no longer supported.
   *
   * @deprecated
   */
  subject?: string;
  /** Optional. Button linking to a URL with context on this payment. */
  button?: Button;
  /**
   * Optional. Description of payment in email message.
   *
   * At most 255 characters.
   */
  amount_caption?: string;
  /**
   * Optional. Text describing this payment in the bank statement.
   *
   * At most 38 characters.
   */
  statement_description?: string;
}

export interface Button {
  /** Button display label. At most 64 characters. */
  label?: string;
  /** URL for button to link to. At most 1024 characters. */
  link?: string;
}

/** A client's view of a transfer between instruments. */
export interface Transfer {
  token?: string;
  source?: Instrument;
  target?: Instrument;
  state?: TransferState;
  amount?: Money;
  /** All dates are in milliseconds since 1/1/1970 UTC. */
  created_at?: number;
  completed_at?: number;
  failed_at?: number;
  push_amount?: Money;
}

export enum TransferState {
  FAILED = "FAILED",
  PROCESSING = "PROCESSING",
  COMPLETE = "COMPLETE",
  WAITING_ON_CUSTOMER = "WAITING_ON_CUSTOMER",
}

export interface Payment {
  /**
   * Deprecated. Replaced with id.
   *
   * @deprecated
   */
  payment_id?: string;
  /** The client-defined ID of the request. */
  id?: string;
  /** Either SEND or REQUEST. */
  action?: Action;
  /** Sender of cash. */
  sender?: CashCustomer;
  /** Recipient of cash. */
  recipient?: CashCustomer;
  /** The amount of the payment. Must be positive. */
  amount_money?: Money;
  /** The amount charged on the sender's card. Must be positive. */
  sender_amount_money?: Money;
  /** The amount deposited onto the recipient's card. Must be positive. */
  recipient_amount_money?: Money;
  /**
   * Initiator's note, such as "dinner on wednesday". Only populated in detailed payment views such
   * as the ListPayment and GetPayment RPCs.
   */
  note?: string;
  /** Current state of the payment. */
  state?: PaymentState;
  /**
   * Why this payment was canceled. Null unless the payment's state is CANCELED.
   * If the reason is DECLINED, the recipient shouldn't be informed of this
   * payment.
   */
  cancellation_reason?: PaymentCancellationReason;
  /** ISO8601 formatted UTC DateTime. */
  created_at?: string;
  /** When we pulled money from the sender's card or bank account. */
  captured_at?: string;
  /** When we pushed money to the recipient's card or bank account. */
  paid_out_at?: string;
  /** When this payment transitioned to the CANCELED state. */
  canceled_at?: string;
  /**
   * When we refunded money back to the sender's card in the event of an
   * unsuccessful payment.
   */
  refunded_at?: string;
  /**
   * When the payment is expected to arrive in the recipient's bank account. Null until the
   * payout instrument is known.
   */
  estimated_payout_received_at?: string;
  /** Non-null if this payment has reached the sender and should appear in their activity. */
  reached_sender_at?: string;
  /** Non-null if this payment has reached the recipient and should appear in their activity. */
  reached_recipient_at?: string;
  /**
   * Role of the caller on this payment, or null if the caller is not a customer or has no
   * particular role on this payment.
   */
  role?: Role;
  /**
   * Current blockers for the caller on this payment, or null if the payment is
   * not blocked on the caller.
   *
   * This field is omitted by the list payments endpoint.
   */
  blockers?: Blockers;
  /**
   * The instrument that the sender used for this payment. This field is only populated once the
   * sender's instrument is locked in, and if the caller is the sender.
   */
  sender_instrument?: Instrument;
  /**
   * The instrument selected to payout this payment. This field is only populated once the
   * recipient's instrument is locked in, and if the caller is the recipient.
   */
  recipient_instrument?: Instrument;
  /** If this is a claimable payment, contains data needed to provide the getter with a claim link. */
  claim_data?: ClaimData;
}

export enum PaymentState {
  /** PENDING - The payment is being processed by Square. */
  PENDING = "PENDING",
  /** WAITING_ON_SENDER - Blocked by the customer sending the funds. */
  WAITING_ON_SENDER = "WAITING_ON_SENDER",
  /** WAITING_ON_RECIPIENT - Blocked by the customer receiving the funds. */
  WAITING_ON_RECIPIENT = "WAITING_ON_RECIPIENT",
  /** WAITING_TO_PAY_OUT - Internal-only. */
  WAITING_TO_PAY_OUT = "WAITING_TO_PAY_OUT",
  /** WAITING_ON_RISK - The payment is in-review waiting on risk decision. */
  WAITING_ON_RISK = "WAITING_ON_RISK",
  /**
   * COMPLETED - All done. Funds have been pulled from the sender and pushed to the
   * recipient.
   */
  COMPLETED = "COMPLETED",
  /**
   * WAITING_TO_REFUND - The recipient has initiated a refund of this payment to the sender. The refund is processing
   * or is scheduled to be processed.
   */
  WAITING_TO_REFUND = "WAITING_TO_REFUND",
  /**
   * CANCELED - The payment has been canceled. If money has already been pulled from the
   * sender, it will be refunded.
   */
  CANCELED = "CANCELED",
  /**
   * WAITING_ON_EXTERNAL - The payment is waiting on external system (e.g. blockchain confirmation in case of bitcoin
   * withdrawals).
   */
  WAITING_ON_EXTERNAL = "WAITING_ON_EXTERNAL",
  /** SCHEDULED - The payment is scheduled for a future date */
  SCHEDULED = "SCHEDULED",
  /** AUTHORIZED - Payment is authorized and awaiting capture by an external party. */
  AUTHORIZED = "AUTHORIZED",
}

export enum PaymentCancellationReason {
  /** OTHER - The payment was canceled for an unspecified reason. */
  OTHER = "OTHER",
  EXPIRED = "EXPIRED",
  SENDER_CANCELED = "SENDER_CANCELED",
  RECIPIENT_CANCELED = "RECIPIENT_CANCELED",
  SQUARE_CANCELED = "SQUARE_CANCELED",
  LIMIT_EXCEEDED = "LIMIT_EXCEEDED",
  DECLINED = "DECLINED",
  BELOW_LIMIT = "BELOW_LIMIT",
  PAYING_SELF = "PAYING_SELF",
  /** DEPOSIT_MECHANISMS_EXHAUSTED - Internal-only. Used when a client wants instant payout or nothing. */
  DEPOSIT_MECHANISMS_EXHAUSTED = "DEPOSIT_MECHANISMS_EXHAUSTED",
  EXPIRED_WAITING_ON_SENDER = "EXPIRED_WAITING_ON_SENDER",
  EXPIRED_WAITING_ON_RECIPIENT = "EXPIRED_WAITING_ON_RECIPIENT",
  /**
   * BLOCKER_CANCELLED - A blocker became unresolvable and cancelled the payment. For example, this happens when
   * a customer exhausts their attempts to resolve an email verification blocker.
   */
  BLOCKER_CANCELLED = "BLOCKER_CANCELLED",
  /** UNUSUAL_ACTIVITY - The payment was canceled due to unusual activity. */
  UNUSUAL_ACTIVITY = "UNUSUAL_ACTIVITY",
  /** REFUNDED - The payment was refunded from the recipient. */
  REFUNDED = "REFUNDED",
  /** AMOUNT_EXCEEDED_SENDER_LIMIT - Cancellations relating to exceeding a limit. */
  AMOUNT_EXCEEDED_SENDER_LIMIT = "AMOUNT_EXCEEDED_SENDER_LIMIT",
  AMOUNT_EXCEEDED_SENDER_STATE_LIMIT = "AMOUNT_EXCEEDED_SENDER_STATE_LIMIT",
  AMOUNT_EXCEEDED_RECIPIENT_STATE_LIMIT = "AMOUNT_EXCEEDED_RECIPIENT_STATE_LIMIT",
  EXCEEDED_PASSCODE_ATTEMPTS = "EXCEEDED_PASSCODE_ATTEMPTS",
  /** TOO_MANY_RECIPIENTS - Cancellations relating to failures of email payments. */
  TOO_MANY_RECIPIENTS = "TOO_MANY_RECIPIENTS",
  EMAIL_BOUNCED = "EMAIL_BOUNCED",
  SUBJECT_INVALID = "SUBJECT_INVALID",
  REQUEST_IN_SUBJECT = "REQUEST_IN_SUBJECT",
  /** WELCOME_BONUS_ALREADY_A_CUSTOMER - Cancellations relating to failed welcome bonuses. */
  WELCOME_BONUS_ALREADY_A_CUSTOMER = "WELCOME_BONUS_ALREADY_A_CUSTOMER",
  WELCOME_BONUS_NOT_VALID_FOR_ACH = "WELCOME_BONUS_NOT_VALID_FOR_ACH",
  /** DECLINED_INSUFFICIENT_BALANCE - Cancellations relating to declined issued card transactions. */
  DECLINED_INSUFFICIENT_BALANCE = "DECLINED_INSUFFICIENT_BALANCE",
  DECLINED_POTENTIALLY_INSUFFICIENT_BALANCE = "DECLINED_POTENTIALLY_INSUFFICIENT_BALANCE",
  DECLINED_BY_SQUARE = "DECLINED_BY_SQUARE",
  DECLINED_CARD_DISABLED = "DECLINED_CARD_DISABLED",
  DECLINED_EXPIRATION_OR_SECURITY_CODE_INCORRECT = "DECLINED_EXPIRATION_OR_SECURITY_CODE_INCORRECT",
  DECLINED_CARD_NOT_ACTIVATED = "DECLINED_CARD_NOT_ACTIVATED",
  DECLINED_AVS_FAILED = "DECLINED_AVS_FAILED",
  DECLINED_CURRENCY_UNSUPPORTED = "DECLINED_CURRENCY_UNSUPPORTED",
  DECLINED_INVALID_PIN = "DECLINED_INVALID_PIN",
  DECLINED_INSTRUMENT_EXPIRED = "DECLINED_INSTRUMENT_EXPIRED",
  /**
   * ABUSE_REPORTED_ON_USER - Cancellations due to reported abuse. This is only shown to the customer who reported
   * the abuse.
   */
  ABUSE_REPORTED_ON_USER = "ABUSE_REPORTED_ON_USER",
  ABUSE_REPORTED_ON_TRANSACTION = "ABUSE_REPORTED_ON_TRANSACTION",
  /** ACH_DECLINED_INSUFFICIENT_FUNDS - Cancellation reasons for ACH Cash Ins */
  ACH_DECLINED_INSUFFICIENT_FUNDS = "ACH_DECLINED_INSUFFICIENT_FUNDS",
  ACH_DECLINED_INVALID_BANK_ACCOUNT = "ACH_DECLINED_INVALID_BANK_ACCOUNT",
  ACH_DECLINED_EXPIRED_BANK_ACCOUNT = "ACH_DECLINED_EXPIRED_BANK_ACCOUNT",
  ACH_DECLINED_FROZEN_BANK_ACCOUNT = "ACH_DECLINED_FROZEN_BANK_ACCOUNT",
  ACH_DECLINED_OTHER = "ACH_DECLINED_OTHER",
  /** INTERNATIONAL_WIRE_TRANSFER - Cancellation relating to wires */
  INTERNATIONAL_WIRE_TRANSFER = "INTERNATIONAL_WIRE_TRANSFER",
}

/**
 * The status of a customer. When the customer can take an action to complete
 * enrollment or payments, those actions are described by blockers. This also includes information
 * about the current linked card and security settings.
 *
 * GET /v1/:customer_id/cash
 * GET /v1/me/cash
 *
 * Headers
 *   Authorization: Client <client secret>
 *   Authorization: Bearer <access token>
 */
export interface Status {
  /** The customer being reported on. */
  customer?: CashCustomer;
  /** The payments being completed. Empty if no payments are being completed. */
  payments?: Payment[];
  /** Blockers to be completed, or null if there is no action to take at this time. */
  blockers?: Blockers;
  /** Information about the card that can be displayed to the customer. */
  card_summary?: CardSummary;
  /** True if payments must be confirmed with a passcode before they are sent. */
  passcode_confirmation_enabled?: boolean;
  /** The display name of the customer. */
  full_name?: string;
  /** Number of payments completed for this customer. */
  number_of_payments?: number;
  /** The money in the customer's balance, if one is present. */
  balance?: Money;
}

export interface PasscodeRequest {
  passcode_confirmation_enabled?: boolean;
  passcode?: string;
}

export interface PasscodeResponse {
  /**
   * A token that can be supplied when initiating a payment to skip passcode confirmation for that
   * payment.
   */
  passcode_token?: string;
}

export interface ProfileWebsiteEnabledRequest {
  enabled?: boolean;
}

export interface ProfileWebsiteEnabledResponse {
}

export interface ProfilePhotoRequest {
  /** Image data using data URI scheme. See http://en.wikipedia.org/wiki/Data_URI_scheme. */
  image_data?: string;
}

export interface ProfilePhotoResponse {
  image_url?: string;
}

export interface Image {
  url?: string;
  width?: number;
  height?: number;
}

/**
 * A non-empty set of possible actions that can be taken by the requesting
 * customer to advance their situation. Blockers apply to customers (that wish
 * to sign up with Cash) or to their payments (that need to be completed).
 *
 * Multiple blockers may be non-null. In this case the blockers are
 * alternatives: complete any one of them to advance. For example, the blockers
 * may include both card and passcode_verification. The customer can enter a new
 * card or verify the existing card; either action will advance the situation.
 *
 * Blockers can be completed in-application or on the web. Compete blockers on
 * the web by visiting the url.
 */
export interface Blockers {
  /**
   * Visit this URL in a browser to complete the customer's blockers. Visiting
   * this URL doesn't require additional authentication, and so it should only
   * be shared with the customer who is blocked. This URL may only be valid for
   * a limited time.
   */
  url?: string;
  /** If non-null, this situation is blocked and cannot be resolved. */
  unresolvable?: UnresolvableBlocker;
  /**
   * Deprecated. Please use AliasBlocker instead.
   * If non-null, this situation may be advanced by linking an email address.
   */
  email?: EmailBlocker;
  /** If non-null, this situation may be advanced by verifying an email address. Internal-only. */
  email_verification?: EmailVerificationBlocker;
  /** If non-null, this situation may be advanced by passcode verification. */
  passcode_verification?: PasscodeVerificationBlocker;
  /** If non-null, this situation may be advanced by verifying a previously linked instrument. */
  instrument_verification?: InstrumentVerificationBlocker;
  /**
   * If non-null, this situation may be advanced by setting a passcode. If the customer already has
   * a passcode, the existing passcode will need to be supplied along with a new passcode.
   */
  passcode_creation?: PasscodeCreationBlocker;
  /** If non-null, this situation may be advanced by linking a card. */
  card?: CardBlocker;
  /**
   * If non-null, this situation may be advanced by providing a copy of a government ID.
   * The apps may have a client side library to parse the information from the images and send it
   * to the server. The server might perform its own recognition to verify that the information
   * passed matches the information on the images.
   */
  government_id?: GovernmentIdBlocker;
  /** If non-null, this situation may be advanced by identity verification. */
  identity_verification?: IdentityVerificationBlocker;
  /** If non-null, this situation may be advanced by selecting a rate plan. */
  rate_plan?: RatePlanBlocker;
  /**
   * Deprecated. Please use AliasBlocker instead.
   * If non-null, this situation may be advanced by linking an SMS number. Internal-only.
   */
  phone_number?: PhoneNumberBlocker;
  /** If non-null, this situation may be advanced by verifying an SMS number. Internal-only. */
  phone_verification?: PhoneVerificationBlocker;
  /** A payment requires confirmation for spoofing, duplication, etc. Internal-only. */
  confirm?: ConfirmBlocker;
  /** If non-null, this situation may be advanced by setting the customer's name. */
  name?: NameBlocker;
  /** If non-null, this situation is blocked on selecting a cashtag. Internal-only. */
  cashtag?: CashtagBlocker;
  /** If non-null, this situation is blocked on resolving a merge. Internal-only. */
  resolve_merge?: ResolveMergeBlocker;
  /** If non-null, this situation is blocked on providing campaign data. */
  campaign?: CampaignBlocker;
  /**
   * Not supported by clients.
   *
   * @deprecated
   */
  ios_donation?: IosDonationBlocker;
  /** If non-null, this situation may be advanced by supplying a reward code. Internal-only. */
  reward_code?: RewardCodeBlocker;
  /**
   * If non-null, this situation is blocked until the customer contacts support and we manually
   * resolve this blocker. Internal-only.
   */
  support_required?: SupportRequiredBlocker;
  /**
   * If non-null, this situation may be advanced by providing the parts of a customer's address
   * specified by the blockers flags.
   */
  address?: AddressBlocker;
  /** If non-null, the situation is blocked on user selecting one of the options in the blocker. */
  selection?: SelectionBlocker;
  /** If non-null, this situation is blocked until a recipient claims this payment. Internal-only. */
  claim_required?: ClaimRequiredBlocker;
  /**
   * If non-null, this situation is blocked on the user selecting if they'd like to begin the
   * process of provisioning their issued card with Apple Pay. This blocker can be skipped by
   * clients if Apple Pay provisioning isn't supported at this time (only the client has the full
   * context to know if it is currently supported).
   */
  apple_pay_selection?: ApplePaySelectionBlocker;
  /**
   * If non-null, this situation is blocked on the native iOS flow for provisioning their issued
   * card with Apple Pay.
   */
  apple_pay_provisioning?: ApplePayProvisioningBlocker;
  /** If non-null, this situation is blocked on the user entering their signature. */
  signature?: SignatureBlocker;
  /**
   * If non-null, this situation is blocked on the user providing the QR code on their physical
   * card.
   *
   * DUE TO AN APP BUG NO BLOCKER_DESCRIPTOR_ID IS ECHOED BACK TO THE SERVER FOR THIS BLOCKER.
   * ALL BLOCKER_DESCRIPTOR_IS REFERENCES SERVER SIDE SHOULD BE HARDCODED TO "qr_code".
   */
  qr_code?: QrCodeBlocker;
  /**
   * If non-null, this situation is blocked on the user providing the CVV and expiry date on their
   * physical card.
   *
   * DUE TO AN APP BUG NO BLOCKER_DESCRIPTOR_ID IS ECHOED BACK TO THE SERVER FOR THIS BLOCKER.
   * ALL BLOCKER_DESCRIPTOR_IS REFERENCES SERVER SIDE SHOULD BE HARDCODED TO
   * "card_passcode_and_expiration".
   */
  card_passcode_and_expiration?: CardPasscodeAndExpirationBlocker;
  /** If non-null, this situation is blocked on the user uploading a file, such as a selfie. */
  file?: FileBlocker;
  /** If non-null, this situation is blocked on the user verifying their contacts. */
  contact_verification?: ContactVerificationBlocker;
  /** If non-null, this situation is blocked on the user selecting a region. */
  region?: RegionBlocker;
  /**
   * If non-null, this situation is blocked on the user specifying a
   * scheduled transaction preference (scheduled reload aka auto add cash).
   */
  scheduled_transaction?: ScheduledTransactionBlocker;
  cash_waiting?: CashWaitingBlocker;
  invite_friends?: InviteFriendsBlocker;
  /**
   * If non-null, this situation is blocked on the native Android flow
   * for provisioning their issued card with Google Pay.
   */
  google_pay_provisioning?: GooglePayProvisioningBlocker;
  card_customization?: CardCustomizationBlocker;
  order_confirmation?: OrderConfirmationBlocker;
  form?: FormBlocker;
  disclosure?: DisclosureBlocker;
  card_design?: CardDesignBlocker;
  /**
   * If non-null, this flow is blocked on the user authenticating against the NuData widget in
   * order to follow the 3-D Secure specification.
   */
  nudata_blocker?: NuDataBlocker;
  alias_blocker?: AliasBlocker;
  check_deposit_blocker?: CheckDepositBlocker;
  three_domain_secure_redirect_blocker?: ThreeDomainSecureRedirectBlocker;
  amount_blocker?: AmountBlocker;
  google_pay_complete_provisioning_blocker?: GooglePayCompleteProvisioningBlocker;
  transaction_picker_blocker?: TransactionPickerBlocker;
  pay_with_cash_authorization_blocker?: PayWithCashAuthorizationBlocker;
  three_domain_secure_v2_blocker?: ThreeDomainSecureV2Blocker;
  paper_cash_deposit_blocker?: PaperCashDepositBlocker;
  tax_web_view_blocker?: TaxWebViewBlocker;
  /** If non-null, this situation may be advanced by identity verification. */
  select_sponsors_blocker?: SelectSponsorsBlocker;
  pinwheel_link_blocker?: PinwheelLinkBlocker;
  /**
   * If non-null, this situation is blocked on the customer creating a new password,
   * which is different and longer-form than the `PasscodeCreationBlocker`.
   *
   * This blocker can be resolved with biometrics on the client, if available.
   */
  password_creation_blocker?: PasswordCreationBlocker;
  /**
   * If non-null, this situation is blocked on the customer verifying their password,
   * which is different and longer-form than the `PasscodeVerificationBlocker`.
   *
   * If a customer already has a password set and wants to change it, a `PasswordVerificationBlocker`
   * should first be sent and then a `PasswordCreationBlocker` should be sent.
   */
  password_verification_blocker?: PasswordVerificationBlocker;
  tutorial_blocker?: TutorialBlocker;
  payroll_provider_search_blocker?: PayrollProviderSearchBlocker;
  instant_pay_auto_advance_blocker?: InstantPayAutoAdvanceBlocker;
  persona_didv_blocker?: PersonaDidvBlocker;
  round_up_onboarding_blocker?: RoundUpOnboardingBlocker;
  change_round_up_destination_blocker?: ChangeRoundUpDestinationBlocker;
  /** If non-null, this situation is blocked on the user selecting something from their activity feed. */
  activity_picker_blocker?: ActivityPickerBlocker;
  apple_pay_complete_provisioning_blocker?: ApplePayCompleteProvisioningBlocker;
  fileset_upload_blocker?: FilesetUploadBlocker;
  instrument_selection_blocker?: InstrumentSelectionBlocker;
  instant_pay_direct_deposit_switch_blocker?: InstantPayDirectDepositSwitchBlocker;
  /**
   * If non-null, this flow is blocked on an action performed in a webview
   * where completion is marked by reaching a given callback URL.
   */
  webview_blocker?: WebviewBlocker;
  direct_deposit_setup_blocker?: DirectDepositSetupBlocker;
  multi_currency_amount_entry_blocker?: MultiCurrencyAmountEntryBlocker;
  retailer_map_blocker?: RetailerMapBlocker;
  participant_selection_blocker?: ParticipantSelectionBlocker;
  /** Not real yet, do not use. */
  treehouse_blocker?: TreehouseBlocker;
  recurring_payment_blocker?: RecurringPaymentBlocker;
  clabe_entry_blocker?: ClabeEntryBlocker;
  overflow_option_picker_blocker?: OverflowOptionPickerBlocker;
  multi_currency_payment_review_blocker?: MultiCurrencyPaymentReviewBlocker;
  credit_multi_step_loading_blocker?: CreditMultiStepLoadingBlocker;
  credit_animation_blocker?: CreditAnimationBlocker;
  onboarding_internal_route_blocker?: OnboardingInternalRouteBlocker;
  lending_null_state_blocker?: LendingNullStateBlocker;
  cash_app_pay_decline_prevention_blocker?: CashAppPayDeclinePreventionBlocker;
  account_recovery_menu_blocker?: AccountRecoveryMenuBlocker;
  sponsor_selection_details_blocker?: SponsorSelectionDetailsBlocker;
  payment_plan_data_blocker?: PaymentPlanDataBlocker;
  set_paycheck_allocation_amount_blocker?: SetPaycheckAllocationAmountBlocker;
  recipient_selector_blocker?: RecipientSelectorBlocker;
  credit_first_time_borrow_blocker?: CreditFirstTimeBorrowBlocker;
  investment_entity_selection_blocker?: InvestmentEntitySelectionBlocker;
}

export interface CashWaitingBlocker {
}

export interface InviteFriendsBlocker {
  invite_friends_opportunity_screen?: InviteFriendsOpportunityScreen;
  contact_access_preparation_screen?: ContactAccessPreparationScreen;
  invite_friends_screen?: InviteFriendsScreen;
  recommended_contacts_hashed_alias_ids?: string[];
}

export interface InviteFriendsOpportunityScreen {
  treatment?: string;
  header_text?: string;
  body_text?: string;
  skip_button_text?: string;
  continue_button_text?: string;
  button_order?: InviteFriendsBlockerButtonOrder;
}

export interface ContactAccessPreparationScreen {
  treatment?: string;
  header_text?: string;
  body_text?: string;
  skip_button_text?: string;
  continue_button_text?: string;
  button_order?: InviteFriendsBlockerButtonOrder;
}

export interface InviteFriendsEnableReferralText {
  title?: string;
  subtitle?: string;
  redemption_steps?: string[];
  footer?: string;
}

export interface InviteFriendsScreen {
  treatment?: string;
  /**
   * This is used by the app to select a region-specific text message based on the phone number
   * of the recipient.
   */
  sms_text_by_country?: CountryText[];
  invite_contact_button_text?: string;
  invited_contact_toast_subtitle?: string;
  contact_access_request_text?: string;
  contact_access_request_button_text?: string;
  send_invite_button_text?: string;
  header_text?: string;
  sharing_content?: SharingContent;
  button_order?: InviteFriendsBlockerButtonOrder;
  /**
   * Used for rendering a detailed explanation of what steps are required
   * for the customer (and for their referees) to successfully complete a referral.
   */
  enable_referral_text?: InviteFriendsEnableReferralText;
}

export interface SharingContent {
  /** Used for all sharing in Android and public sharing in iOS. */
  default_text?: string;
  /** Used for sharing via message-type apps in iOS. */
  direct_text?: string;
  /** The URL needed for sharing to Facebook in iOS. */
  url?: string;
  /** The email subject for sharing on iOS. */
  email_subject?: string;
}

export interface ScheduledTransactionBlocker {
  requires_amount?: boolean;
  requires_frequency?: boolean;
  minimum_amount?: Money;
  maximum_amount?: Money;
  type?: ScheduledTransactionPreferenceType;
  investment_entity_token?: string;
}

/**
 * Allows the representation of real numbers trading range for precision eg:
 *
 *  value -> significand, magnitude -> math
 *  1     -> 1, 0                   -> 1 * 10^0
 *  100   -> 1, 2                   -> 1 * 10^2
 *  60    -> 6, 1                   -> 6 * 10^1
 *  60    -> 60, 0                  -> 60 * 10^0 (also valid representation)
 *  23.45 -> 2345, -2               -> 2345 * 10^-2
 *
 * To preserve the precision of this value do not convert it to a scalar such as an int or float,
 * and instead use something like BigDecimal (Java/Kotlin) or NSDecimalNumber (Swift)"
 */
export interface ScientificNumber {
  significand?: number;
  magnitude?: number;
}

export interface AmountBlocker {
  title?: string;
  subtitle?: string;
  money_config?: AmountBlockerMoneyConfig | undefined;
  percentage_config?: AmountBlockerPercentageConfig | undefined;
  confirm_button_text?: string;
  help_action?: BlockerAction;
}

export interface AmountBlockerMoneyConfig {
  minimum_amount?: Money;
  maximum_amount?: Money;
  prefilled_amount?: Money;
}

export interface AmountBlockerPercentageConfig {
  minimum_amount?: ScientificNumber;
  maximum_amount?: ScientificNumber;
}

/** Used in cross-region scenarios where different text is needed depending on the target region. */
export interface CountryText {
  country?: Country;
  text?: string;
}

export interface NameBlocker {
}

export interface EmailBlocker {
  /** The email address to prefill, or null if none is known. */
  email?: string;
}

export interface EmailVerificationBlocker {
  /** The email address being verified. */
  email?: string;
}

export interface PasscodeVerificationBlocker {
  /** The brand of the card being verified. InstrumentType is equivalent to Connect's CardBrand. */
  brand?: InstrumentType;
  /** The last four digits of the card being verified. */
  pan_suffix?: string;
  /** The instrument to be verified. */
  instrument?: Instrument;
}

export interface InstrumentVerificationBlocker {
  /**
   * The types of instruments that can be verified to unblock the scenario.
   *
   * For passcode resets there will only be one instrument type present.
   */
  instrument_type?: CashInstrumentType[];
}

export interface PasscodeCreationBlocker {
  /** True if the customer must provide an existing passcode or passcode token. */
  requires_existing_passcode?: boolean;
  /**
   * The instrument token of the instrument whose passcode or passcode token must be
   * supplied. This is null when requires_passcode is false.
   */
  instrument_token?: string;
}

export interface CardBlocker {
  card_status?: CardStatus;
  /**
   * A list of supported instrument types that can unblock this payment. At present, this list
   * will always include DEBIT_CARD. For businesses receiving this blocker for a payment or for
   * onboarding, the list will also include BANK_ACCOUNT.
   */
  supported_instrument_types?: CashInstrumentType[];
}

export interface RegionBlocker {
  /** Countries suggested for customer. */
  suggested_countries?: Country[];
}

export interface IdentityVerificationBlocker {
  /** True if this blocker requires a birthdate. */
  requires_birthdate?: boolean;
  /**
   * True if this blocker requires a full 9-digit SSN. Deprecated by requires_full_ssn.
   *
   * @deprecated
   */
  requires_ssn?: boolean;
  /** True if this blocker requires a full 9-digit SSN. */
  requires_full_ssn?: boolean;
  /** True if this blocker requires only the last 4 digits of SSN. */
  requires_last_four_ssn?: boolean;
  /** True if this blocker requires a full name. */
  requires_full_name?: boolean;
  /** True if this blocker requires an address. */
  requires_address?: boolean;
}

/** Configurable sponsor selection blocker */
export interface SelectSponsorsBlocker {
  /** True if only cash users should appear in the list */
  allow_only_cash_users?: boolean;
  /**
   * True if multiple sponsor selections can be made
   *
   * @deprecated
   */
  allow_multiple?: boolean;
  /** The title text at the top of the blocker */
  title?: string;
  /** The hint text in the search field */
  to_hint?: string;
  /**
   * True if only cash users should get recommended. It should be false when the single
   * handshake onboarding experience for out-of-network sponsors is enabled.
   */
  should_only_recommend_cash_users?: boolean;
  /** The 'Add Contacts' dialog prompt */
  contacts_dialog?: SelectSponsorsBlockerAddContactsDialog;
  /** The 'Add Contacts' inline card that shows when the user dismisses dialog without adding contacts */
  contacts_card?: SelectSponsorsBlockerAddContactsCard;
}

export interface SelectSponsorsBlockerAddContactsDialog {
  /** The title of the dialog */
  title?: string;
  /** The text body of the dialog */
  body?: string;
  /** The add_contact_button_title button, shown on the right, or top if vertically stacked. */
  add_contact_button_title?: string;
  /** The dismiss_button_title button, shown on the left, or bottom if vertically stacked */
  dismiss_button_title?: string;
}

export interface SelectSponsorsBlockerAddContactsCard {
  /** The inline text on add contacts card to prompt user to add contacts */
  inline_text?: string;
  /** The add_contact_button_title button that pulls user's contacts into app */
  add_contact_button_title?: string;
}

/** Displays details of a selected sponsor and allows dependents to send a sponsorship request */
export interface SponsorSelectionDetailsBlocker {
  sponsor_avatar?: FormBlockerElementAvatarElement;
  /** The title text at the top of the blocker */
  title?: FormBlockerElementTextElement;
  /** The text shown under the title */
  subtitle?: FormBlockerElementTextElement;
  /** The rows consisting of an icon and a text that show details of the selected sponsor */
  sponsor_details?: FormBlockerElementSelectableRowElement[];
  /** The title of the primary button at the bottom of the screen */
  primary_button_text?: string;
  /** The action to continue to the next step in the flow */
  confirm_action?:
    | SponsorSelectionDetailsBlockerConfirmSelectedSponsorAction
    | undefined;
  /** The action to send a sms to the phone number of the selected sponsor */
  send_sms_action?:
    | SponsorSelectionDetailsBlockerSendSelectedSponsorSmsAction
    | undefined;
  /**
   * The action to cancel the sponsor selection when tapping on the cancel button.
   * If it is null, the cancel button is hidden.
   */
  cancel_action?: SponsorSelectionDetailsBlockerCancelSponsorSelectionAction;
  /** The title of the cancel button at the bottom of the screen */
  cancel_button_text?: string;
}

export interface SponsorSelectionDetailsBlockerSendSelectedSponsorSmsAction {
  selected_sponsor_sms_number?: string;
  message?: string;
  /** The action to continue to the next step in the flow after sending the sms */
  action_after_sending_sms?: SponsorSelectionDetailsBlockerConfirmSelectedSponsorAction;
}

export interface SponsorSelectionDetailsBlockerConfirmSelectedSponsorAction {
  selected_sponsor?: UiCustomer;
  /** Should be nonnull when the selected sponsor is not a cash customer. Otherwise, it should be null */
  invite_code?: string;
}

export interface SponsorSelectionDetailsBlockerCancelSponsorSelectionAction {
  /** Should be nonnull when the selected sponsor is not a cash customer. Otherwise, it should be null */
  invite_code?: string;
}

/** Describes a help item text and action. */
export interface HelpItem {
  text?: string;
  localized_text?: LocalizableString;
  /** The action to take when this help option is selected. */
  action?: HelpItemAction;
  /**
   * The URL to navigate to when the item is selected.
   * Must be populated when action is OPEN_URL.
   */
  url?: string;
  /**
   * The action to pass to SelectOption when the item is selected.
   * Must be populated when action is SELECT_OPTION.
   */
  select_option_action?: string;
  /**
   * If non-null, this text should be displayed in a dialog with Ok/Cancel options before
   * performing the action.
   */
  confirm_dialog_text?: string;
  /**
   * The optional starting token to pass to StartSupportFlow, if action is START_SUPPORT_FLOW.
   *
   * @deprecated
   */
  support_node_token?: string;
  /**
   * The client scenario to switch to, if the action is START_CLIENT_SCENARIO.
   *
   * @deprecated
   */
  client_scenario?: string;
  /** Title of the inline message. Must be supplied if action is SHOW_MESSAGE. */
  message_title?: string;
  /** Text of the inline message. Must be supplied if action is SHOW_MESSAGE. */
  message_text?: string;
  /** Action to perform. Must be supplied if action is BLOCKER_ACTION. */
  blocker_action?: BlockerAction;
}

/** Use BLOCKER_ACTION for deprecated actions. */
export enum HelpItemAction {
  /**
   * GET_SCENARIO_PLAN - Call GetScenarioPlan and continue with the returned scenario plan.
   *
   * @deprecated
   */
  GET_SCENARIO_PLAN = "GET_SCENARIO_PLAN",
  /**
   * RESOLVE_MERGE_FALSE - Call the resolve merge api with confirm_merge=false.
   *
   * @deprecated
   */
  RESOLVE_MERGE_FALSE = "RESOLVE_MERGE_FALSE",
  /**
   * SKIP_BLOCKER - Call the SkipBlocker api.
   *
   * @deprecated
   */
  SKIP_BLOCKER = "SKIP_BLOCKER",
  /**
   * OPEN_URL - Open the URL in the url field.
   *
   * @deprecated
   */
  OPEN_URL = "OPEN_URL",
  /** SELECT_OPTION - Call SelectOption with the action in select_option_action field. */
  SELECT_OPTION = "SELECT_OPTION",
  /**
   * SIGN_OUT - Clear the session, flow token, and return to the sign in screen.
   *
   * @deprecated
   */
  SIGN_OUT = "SIGN_OUT",
  /**
   * START_SUPPORT_FLOW - Open the in-app support flow, passing in the support_node_token if supplied.
   *
   * @deprecated
   */
  START_SUPPORT_FLOW = "START_SUPPORT_FLOW",
  /**
   * START_CLIENT_SCENARIO - Switch to a new client scenario and call CompleteScenario to proceed.
   *
   * @deprecated
   */
  START_CLIENT_SCENARIO = "START_CLIENT_SCENARIO",
  /**
   * SHOW_MESSAGE - Show an inline message with message_title and message_text.
   *
   * @deprecated
   */
  SHOW_MESSAGE = "SHOW_MESSAGE",
  /** BLOCKER_ACTION - Performs the action specified in blocker_action. */
  BLOCKER_ACTION = "BLOCKER_ACTION",
}

/** Internal-only. */
export interface GovernmentIdBlocker {
  help_items?: HelpItem[];
  /**
   * FormBlocker representing the instructions screen that has to be shown before the actual ID
   * scanning screen. This FormBlocker is required to have a single primary_action, which, when
   * invoked, will always navigate to the ID scanning screen without hitting the server in between.
   */
  pre_license_blocker?: FormBlocker;
  /** The title text to be displayed when scanning the front side of the government ID. */
  front_title?: string;
  /** The title text to be displayed when scanning the back side of the government ID. */
  back_title?: string;
  /** Text to be displayed as the footer on the scanning screen. */
  footer_text?: string;
  /**
   * Small icon to be displayed to the left of the footer_text. footer_text has to be provided for
   * this icon to be displayed.
   */
  footer_icon?: GovernmentIdBlockerFooterIcon;
  /** The title text to be displayed in the "tips" section of the help menu. */
  help_tips_title?: string;
  /** A set of texts to be displayed in the "tips" section of the help menu. */
  help_tips?: string[];
}

export enum GovernmentIdBlockerFooterIcon {
  LOCK = "LOCK",
}

/** Internal-only. */
export interface RatePlanBlocker {
  available_rate_plans?: RatePlan[];
}

/** Internal or Snapchat. */
export interface PhoneNumberBlocker {
}

/** Internal or Snapchat. */
export interface PhoneVerificationBlocker {
  /** The SMS number being verified. */
  number?: string;
}

export interface ConfirmBlocker {
  /** True if the customer must confirm the payment with their debit card's passcode. */
  requires_passcode?: boolean;
  /** The brand of the card to be verified by passcode. This is null unless passcode is required. */
  brand?: InstrumentType;
  /**
   * The last four digits of the card to be verified by passcode. This is null unless passcode is
   * required.
   */
  pan_suffix?: string;
  /** The instrument token selected for the payment. This is null unless passcode is required. */
  instrument_token?: string;
  /** True if the sender must confirm a bill. */
  initiated_by_recipient?: boolean;
  /**
   * Obsolete. This is always false.
   *
   * @deprecated
   */
  initiated_by_platform?: boolean;
  /**
   * True if the initiator must confirm a payment that was detected as a possible duplicate.
   * Typically this occurs when broken email software re-sends the same message twice with two
   * different IDs.
   */
  possible_duplicate?: boolean;
  /**
   * If a payment is suspected of being a duplicate, the payment may be blocked on confirmation,
   * and the earlier payment's token is provided here.
   */
  suspected_duplicate_payment_token?: string;
  /**
   * True if a sender's email payment could not be verified cryptographically.
   *
   * Internal only.
   */
  possible_spoof?: boolean;
  /**
   * True if this payment was unblocked by an unrelated action and should be explicitly confirmed
   * before proceeding. Used to prevent abandoned zombie payments from resurrecting.
   */
  draft?: boolean;
  /**
   * If true, the clients should show an explicit confirmation blocker, ignoring all the above
   * blockers. If requires_passcode is also true, the client should ask the customer for passcode
   * confirmation if they indicate that they want to confirm when showing the explicit confirmation
   * screen.
   */
  explicit_confirmation?: boolean;
}

/** Internal-only. */
export interface CashtagBlocker {
  /** A suggested cashtag for the customer. May be null if no good suggestion is available. */
  suggested_cashtag?: string;
  main_text?: string;
}

/** Internal-only. */
export interface ResolveMergeBlocker {
}

/** Internal-only. */
export interface CampaignSupporterData {
  full_name?: string;
  occupation?: string;
  employer?: string;
  street_address?: string;
  city?: string;
  state?: string;
  zip?: string;
}

/** Internal-only. */
export interface CampaignBlocker {
  suggestion?: CampaignSupporterData;
}

/** Internal-only. */
export interface RewardCodeBlocker {
  /** A suggested reward code if relevant invitations are registered on the server. May be null. */
  suggested_code?: string;
  /** The minimum length a valid reward code can be. */
  minimum_code_length?: number;
}

/** Internal-only. */
export interface SupportRequiredBlocker {
}

export interface AddressBlocker {
  /** True if the customer must provide a postal code (zip code) to advance the situation. */
  requires_postal_code?: boolean;
  /**
   * True if the customer must provide street address. If this is True, requires_postal_code
   * must be true.
   */
  requires_street_address?: boolean;
  /** True if the customer must provide city (GlobalAddress.locality) to advance the situation. */
  requires_city?: boolean;
  /**
   * True if the customer must provide state (GlobalAddress.administrative_district_level_1) to
   * advance the situation.
   */
  requires_state?: boolean;
}

/**
 * A generic selection blocker that can be used to draw a blocker screen with
 * question and buttons that a user can click. Once a user clicks on the button,
 * SelectOption must be called with the action while setting the
 * client scenario.
 */
export interface SelectionBlocker {
  /** If non-null, icon maps to an image on the client. */
  icon?: SelectionBlockerIcon;
  /**
   * If non-null, display this amount where the icon would be placed. At most one of icon and
   * amount can be non-null.
   * iOS 2.20+ is expected to support this, Android support should come when BTC work is done.
   */
  amount?: Money;
  /** If non-null, the header text is to be shown on the client. */
  header_text?: string;
  /**
   * If non-null, the main text is to be shown under the heading text. This field can contain
   * PII (particularly around addresses) so we shouldn't log it.
   */
  main_text?: string;
  /**
   * If non-null, the footer text is to be shown just above the button(s). This text may include
   * markdown-style links that the client must parse and linkify.
   * Example: "By continuing you agree to the [Terms](https://cash.app/legal/tos)"
   */
  footer_text?: string;
  /** If non-null, the primary option should be placed in the navigation bar to the right. */
  primary_option?: SelectionOption;
  /** If non-null, the secondary option should be placed in the navigation bar to the left. */
  secondary_option?: SelectionOption;
  /**
   * The list of options to display. If non-empty, this list of options should be presented
   * vertically in the center of the screen, and are separate from the primary and secondary
   * options.
   */
  options?: SelectionOption[];
  /** If true, the icon should be above the text, if false the icon should be below the text. */
  icon_above_text?: boolean;
  /**
   * If non-null, the selection blocker is an itemized type with detail rows representing
   * the items.
   */
  detail_rows?: SelectionBlockerDetailRow[];
}

export enum SelectionBlockerIcon {
  INSTANT = "INSTANT",
  ISSUED_CARD = "ISSUED_CARD",
  PHYSICAL_CARD = "PHYSICAL_CARD",
  PHYSICAL_CARD_UPSELL = "PHYSICAL_CARD_UPSELL",
  DIRECT_DEPOSIT = "DIRECT_DEPOSIT",
  PENDING = "PENDING",
  SUCCESS = "SUCCESS",
  FAILURE = "FAILURE",
  VERIFICATION_REQUIRED = "VERIFICATION_REQUIRED",
  GOVERNMENT_ID = "GOVERNMENT_ID",
  EMERGENCY_CARD = "EMERGENCY_CARD",
}

export interface SelectionBlockerDetailRow {
  label?: string;
  value?: string;
  /** Action to perform when the customer clicks on the label. */
  label_action?: SelectionBlockerDetailRowAction;
  /**
   * The URL to navigate to when the label item is selected.
   * Must be populated when label action is OPEN_URL.
   *
   * @deprecated
   */
  label_action_url?: string;
  /**
   * Action to perform when the customer clicks on the label.
   * Must be populated when label_action is BLOCKER_ACTION.
   */
  blocker_action?: BlockerAction;
}

export enum SelectionBlockerDetailRowAction {
  /** @deprecated */
  NONE = "NONE",
  /**
   * OPEN_URL - Open the URL in the url field.
   *
   * @deprecated
   */
  OPEN_URL = "OPEN_URL",
  /** BLOCKER_ACTION - Perform action specified by blocker_action. */
  BLOCKER_ACTION = "BLOCKER_ACTION",
}

export interface SelectionOption {
  /** Label text to be shown on the button. */
  label?: string;
  /** The action associated with this option. It must be sent when calling SelectOption api. */
  action?: string;
  /** Client must set this scenario in the header when calling SelectOption api. */
  client_scenario?: ClientScenario;
  /**
   * Hints can be null. If null, call the server with the option but if is CANCEL_BUTTON, end the
   * the flow without making a round trip to the server.
   */
  hint?: SelectionOptionHint;
}

export enum SelectionOptionHint {
  CANCEL_BUTTON = "CANCEL_BUTTON",
}

/** Internal-only. */
export interface ClaimRequiredBlocker {
}

/**
 * A blocker that asks the customer to select whether they want to go through the flow to provision
 * their issued card for Apple Pay.
 */
export interface ApplePaySelectionBlocker {
  /** The main text to display for the blocker. */
  main_text?: string;
  /** The option that represents that the user wants to provision Apple Pay. */
  accept_option?: SelectionOption;
  /** The option that represents that the user wants to decline Apple Pay. */
  decline_option?: SelectionOption;
}

/** Blocker that requires the app to provision an issued card with Apple Pay to resolve. */
export interface ApplePayProvisioningBlocker {
}

/** Blocker that requires the app to complete provisioning a physical card for Apple Pay. */
export interface ApplePayCompleteProvisioningBlocker {
}

/** Blocker that requires the app to provision an issued card with Google Pay to resolve. */
export interface GooglePayProvisioningBlocker {
}

/** Blocker that requires the app to complete provisioning a physical card for Google Pay. */
export interface GooglePayCompleteProvisioningBlocker {
}

/** Blocker that requires the user to enter their signature to resolve. */
export interface SignatureBlocker {
}

/** Blocker that requires user to provide a card design (e.g., signature and optional cashtag). */
export interface CardDesignBlocker {
  /**
   * Whether or not the customer should be given the option to add their
   * cash tag to the card (e.g., whether it'll fit on card or not).
   */
  cashtag_eligible?: boolean;
  /** Whether or not to allow card theme selection. */
  card_theme_eligible?: boolean;
  /** The default card theme to select. */
  default_card_theme?: CardDesignBlockerCardTheme;
  /**
   * Whether or not the cashtag should be displayed at the top right-hand corner of the card in
   * place of the Visa logo.
   */
  cashtag_on_top?: boolean;
}

export enum CardDesignBlockerCardTheme {
  BLACK = "BLACK",
  WHITE = "WHITE",
}

export interface NuDataBlocker {
  threeds_token?: string;
  threeds_proxy_url?: string;
}

/** Blocker for email or sms. */
export interface AliasBlocker {
  main_text?: string;
  sms_hint_text?: string;
  email_hint_text?: string;
  legal_agreement_text?: string;
  mode?: AliasBlockerMode;
}

/**
 * Whether the blocker should allow the customer to input an email address,
 * SMS number, or either.
 */
export enum AliasBlockerMode {
  /** SMS_DEFAULT - Present "Add Mobile Number" and allow the customer to choose "Use Email." */
  SMS_DEFAULT = "SMS_DEFAULT",
  /**
   * EMAIL_ONLY - Only allow the customer to input an email address,
   * with no option to switch to mobile number.
   */
  EMAIL_ONLY = "EMAIL_ONLY",
  /**
   * SMS_ONLY - Only allow the customer to input a mobile number,
   * with no option to switch to email address.
   */
  SMS_ONLY = "SMS_ONLY",
}

/** Blocker that requires the user to verify a QR code to resolve. */
export interface QrCodeBlocker {
  /**
   * Feature flag control for activate-physical-card-initial-qr-code-blocker-use-new-ui-and-call-server-when-skip
   * Rolled out to 100% and removed from Android and iOS in 3.92
   *
   * @deprecated
   */
  missing_qr_code_to_locate_cvv?: boolean;
  info_text_above_scanner?: string;
  info_text_below_scanner?: string;
  button_text?: string;
  localizable_info_text_above_scanner?: LocalizableString;
  localizable_info_text_below_scanner?: LocalizableString;
  localizable_button_text?: LocalizableString;
}

/** Blocker that requires the user to verify the passcode and expiry date of a card to resolve. */
export interface CardPasscodeAndExpirationBlocker {
}

/**
 * Blocker that cannot be resolved. This gets added to flows that cannot advance. Before sending
 * a ScenarioPlan to the client, UnresolvableBlocker should get stripped if its the only blocker,
 * forcing the client to display the StatusResult.
 */
export interface UnresolvableBlocker {
  unresolvable_reason_id?: number;
}

/** Blocker that requires the user to upload a file to resolve. */
export interface FileBlocker {
  /** The category indicates the type of file to upload, such as a selfie, W2, SSN card, etc. */
  category?: FileCategory;
  /** BIN of the customer's payment card. Used by Bouncer Integration, for example. */
  payment_card_bin?: string;
  /**
   * The field indicates that fraud payload should be included when uploading data.
   * Used by Bouncer Integration for example.
   */
  require_fraud_payload?: boolean;
  /** Last four digits of the customer's payment card. Used by Bouncer Integration, for example. */
  payment_card_last4?: string;
  /** Text to be displayed as the footer on the scanning screen. */
  footer_text?: string;
  /**
   * Small icon to be displayed to the left of the footer_text. footer_text has to be provided for
   * this icon to be displayed.
   */
  footer_icon?: FileBlockerFooterIcon;
}

export enum FileBlockerFooterIcon {
  LOCK = "LOCK",
}

/** Blocker that requires the user to verify their contacts. */
export interface ContactVerificationBlocker {
}

/**
 * Blocker that requires the customer to customize their physical card,
 * with a signature and card theme.
 */
export interface CardCustomizationBlocker {
  /** The title text for the style picker when the header is expanded. */
  style_picker_title_text?: string;
  localized_style_picker_title_text?: LocalizableString;
  /** The title text for the style picker when the header is collapsed. */
  style_picker_short_title_text?: string;
  localized_style_picker_short_title_text?: LocalizableString;
  /** Text to display over customizable area (e.g., "Tap to customize"). */
  customization_prompt_text?: string;
  localized_customization_prompt_text?: LocalizableString;
  /** Text to display on cashtag toggle (e.g., "Show Cashtag"). */
  cashtag_toggle_text?: string;
  localized_cashtag_toggle_text?: LocalizableString;
  /** Title above the card customization UI. */
  customization_title_text?: string;
  localized_customization_title_text?: LocalizableString;
  /**
   * The text to display on the card signature screen after a stamp has been added. When non-null, this text should
   * be shown instead of the text that the client would otherwise display.
   */
  stamp_added_text?: string;
  localized_stamp_added_text?: LocalizableString;
  /**
   * The text to display on the card signature screen when customer returns to draw mode after adding stamps. When
   * non-null, this text should be shown instead of the text that the client would otherwise display.
   */
  return_to_draw_mode_text?: string;
  localized_return_to_draw_mode_text?: LocalizableString;
  /** Message in alert shown when user has used too much ink in their customization. */
  too_much_ink_message?: string;
  localized_too_much_ink_message?: LocalizableString;
  /**
   * The maximum ink coverage allowed in the customizable region of the card.
   * Value must be in the range [0,1], where 0 means an empty customization
   * and 1 means completely covered.
   */
  maximum_ink_coverage?: number;
  card_option_sections?: CardCustomizationBlockerCardOptionSection[];
  customization_area?: CardCustomizationDataCustomizationArea;
  /** formatted cashtag including currency symbol (e.g., $, £) to show on user's Cash Cards */
  formatted_cashtag?: string;
  /** if present, the style to automatically navigate to when presented. Used for deep links */
  selected_card_option_identifier?: string;
}

export interface CardCustomizationBlockerCardPreviewText {
  /** Title shown in the card order preview. */
  title?: string;
  localized_title?: LocalizableString;
  /** Body statement shown in the card order preview. */
  body?: string;
  localized_body?: LocalizableString;
  /** Title shown on the customize card button. */
  customize_card_button_title?: string;
  localized_customize_card_button_title?: LocalizableString;
  /** Title shown on the edit customization button. */
  edit_customization_button_title?: string;
  localized_edit_customization_button_title?: LocalizableString;
  /** Title shown on the order card button. */
  order_card_button_title?: string;
  localized_order_card_button_title?: LocalizableString;
}

export interface CardCustomizationBlockerCardOption {
  /** Card theme represented by this option. */
  card_theme?: CardTheme;
  /** Theme for the card controls represented by this option. */
  controls_theme?: CardCustomizationControlsTheme;
  /**
   * Introductory text to describe this option, e.g., "New!".
   * Will display in this order: `intro_text`, `title`, `detail text`.
   */
  intro_text?: string;
  localized_intro_text?: LocalizableString;
  /**
   * Title text to describe this option, e.g., "Glow in the Dark".
   * Will display in this order: `intro_text`, `title`, `detail text`.
   */
  title?: string;
  localized_title?: LocalizableString;
  /**
   * Detail text to describe this option, e.g., "$5".
   * Will display in this order: `intro_text`, `title`, `detail text`.
   */
  detail_text?: string;
  localized_detail_text?: LocalizableString;
  /**
   * Accessibility label for this option.
   *
   * @deprecated
   */
  accessibility_label?: string;
  /**
   * Whether or not the customer should be given the option to add their
   * cash tag to the card (e.g., whether it'll fit on card or not).
   * Deprecated: Use cashtag_display instead
   *
   * @deprecated
   */
  cashtag_eligible?: boolean;
  customization_eligible?: boolean;
  /**
   * Accessibility text for this option.
   * Use accessibility_text instead of accessibility_label to work around an issue with
   * Objective-C proto generation on iOS
   */
  accessibility_text?: string;
  localized_accessibility_text?: LocalizableString;
  /** Card order preview text for this option. */
  card_preview_text?: CardCustomizationBlockerCardPreviewText;
  /**
   * An indicator for identifying whether it is the legacy or full-face customization.
   *
   * @deprecated
   */
  customization_area?: CardCustomizationBlockerCardOptionCustomizationArea;
  /**
   * Whether or not the cashtag should be rendered on the card or let the user choose.
   * - ALWAYS_HIDDEN: Forced not be rendered on the card (eg: because of profanity, too long to fit on the card).
   * - OPTIONAL: Let the customer choose to render/hide the cashtag on the card.
   * - ALWAYS_SHOW: Forced to render the cashtag on the card.
   */
  cashtag_display?: CardCustomizationBlockerCardOptionCashtagDisplay;
  presentationStyle?: CardPresentationStyle;
  /**
   * Whether or not the card is available to order.
   * - AVAILABLE: Card is in stock and users are allowed to order
   * - UNAVAILABLE: Card is not in stock and the client UI should block orders
   */
  card_availability?: CardCustomizationBlockerCardOptionCardAvailability;
  /**
   * Unused
   *
   * @deprecated
   */
  identifier?: string;
  /** The publicly-visible unique identifier for this configuration option, used for deep links */
  external_identifier?: string;
}

export enum CardCustomizationBlockerCardOptionCustomizationArea {
  LEGACY = "LEGACY",
  FULL_FACE = "FULL_FACE",
}

export enum CardCustomizationBlockerCardOptionCashtagDisplay {
  ALWAYS_HIDDEN = "ALWAYS_HIDDEN",
  OPTIONAL = "OPTIONAL",
  ALWAYS_SHOW = "ALWAYS_SHOW",
}

export enum CardCustomizationBlockerCardOptionCardAvailability {
  AVAILABLE = "AVAILABLE",
  UNAVAILABLE = "UNAVAILABLE",
}

export interface CardCustomizationBlockerCardOptionSection {
  /**
   * The text to show in the section header of the style picker. When
   * null, the section header will be hidden.
   */
  header_text?: string;
  localized_header_text?: LocalizableString;
  /**
   * The text to show in the section selector component in the style
   * picker header. When the short titles are null, the selector
   * component will be hidden.
   */
  short_title_text?: string;
  localized_short_title_text?: LocalizableString;
  /** Options in this section of the style picker. */
  options?: CardCustomizationBlockerCardOption[];
}

/** Blocker that requires the user to confirm the details of an order before continuing with a flow. */
export interface OrderConfirmationBlocker {
}

/** Blocker used to show a Short Form Disclosure to the customer. */
export interface DisclosureBlocker {
  /** The title text displayed at the top of the screen. */
  headline_text?: string;
  /** A list of key fees displayed prominently beneath the headline. */
  line_items?: DisclosureBlockerLineItem[];
  /**
   * The main block of text to display on the screen. This text may include markdown-style links
   * that the client must parse and linkify, as well as markdown-style bold text using asterisks.
   * Example: "By continuing you **agree** to the [Terms](https://cash.app/legal/tos)"
   */
  description_text?: string;
  /** Text to be displayed on the accept button before the screen has reached the bottom. */
  scroll_button_text?: string;
  /** Text to be displayed on the accept button once the screen has been scrolled the bottom. */
  accept_button_text?: string;
  /** Title of the more info panel when it is collapsed. */
  more_info_panel_collapsed_title?: string;
  /** Title of the more info panel when it is expanded. */
  more_info_panel_expanded_title?: string;
  /** Line items shown in the more info panel when it is expanded. */
  more_info_panel_line_items?: DisclosureBlockerLineItem[];
  /** Footer text shown in bold at the bottom of the more info panel when it is expanded. */
  more_info_panel_footer_text?: string;
  /**
   * Footer text shown below the more info panel. This text may include markdown-style links that
   * the client must parse and linkify, as well as markdown-style bold text using asterisks.
   * Example: "By continuing you **agree** to the [Terms](https://cash.app/legal/tos)"
   */
  footer_text?: string;
  /** Message to show in a dialog when the customer attempts to cancel out of the blocker. */
  cancel_dialog_text?: string;
  /** Button text for the button to cancel the cancellation dialog. */
  cancel_dialog_cancel_text?: string;
  /** Button text for the button to confirm the cancellation dialog. */
  cancel_dialog_confirm_text?: string;
}

export interface DisclosureBlockerLineItem {
  /** Text displayed in the left column */
  title_text?: string;
  /** Text displayed in the right column */
  detail_text?: string;
}

/** Blocker that prompts customers for a transaction when reporting a payment issue. Deprecated - use ActivityPickerBlocker. */
export interface TransactionPickerBlocker {
  title?: string;
}

/** Blocker that prompts customers to select something from their activity feed. Available on v3.59+. */
export interface ActivityPickerBlocker {
  /** Title to display above the activity feed. */
  title?: string;
  /**
   * Regular expression applied to the UiPayment#token field of items in the activity feed to select items to include.
   * For example, the expression "^T_.*$" could be used to show only transfers (AKA cash ins / outs. See also go/cashtokens.) from the presented options.
   * The regex used must be compatible with the built in Android & iOS regex engines.
   * If this is null then the full activity feed is shown.
   */
  include_token_regex?: string;
}

/**
 * Blocker that allows user to top-up their account to meet the minimum amount needed to complete order
 * and prevent a decline from occurring (hence "decline prevention").
 * Shown to the user when they attempt to make a purchase under the usual grant flow (via
 * PayWithCashAuthorizationBlocker) but don't have enough money in their Cash balance to complete
 * the order.
 *
 * Notion: https://www.notion.so/cashappcash/SVB-Decline-Prevention-7bf3b6657b944694984f217be97dba2f
 * Figma: https://www.figma.com/file/nBsLfxYgyfWaREHJmwoYQv/TopHat-Top-Ups?node-id=1168%3A97185&t=DttXirCDOqtkGxwj-1
 */
export interface CashAppPayDeclinePreventionBlocker {
  /** e.g. "Amount is too low to complete $216.36 order" */
  primaryText?: string;
  /** Shown below primaryText, e.g. "Portrait Coffee" (the business name) */
  secondaryText?: string;
  /** Shown below secondaryText, e.g. "10% off applied". May be empty. */
  tertiaryText?: string;
  /** Shown below tertiaryText, e.g. "Add Cash". Note that there's an 'i' button here that opens up an explanation */
  infoText?: string;
  /** Stepper for selecting amount to top up */
  stepper?: CashAppPayDeclinePreventionBlockerStepperContent;
  /** e.g. "Add to your Cash balance of $195.15 from Mastercard 0955" */
  supplementalText?: string;
  /** The footer buttons' (primary, secondary) blocker actions. */
  footer?: PayWithCashAuthorizationBlockerFooter;
  /** The interstitial shown before the main content. */
  onDisplayInterstitial?: PayWithCashAuthorizationBlockerInterstitial;
  /**
   * The blocker action that fires when the user attempts to top up an amount that doesn't meet the
   * order minimum. By default, this should contain a BlockerAction.ConfirmationSheet and a
   * BlockerAction.SubmitAction (although future changes do not guarantee this).
   *
   * If the user *does* meet the order minimum with the amount they selected, this BlockerAction
   * should *not* fire. Instead, the client should invoke the `/submit-form` endpoint directly with a
   * `SubmitFormRequest.ElementResult.MoneyInputResult` containing the amount the user selected.
   *
   * If this `BlockerAction` is invoked: When the user clicks the Confirm button on the confirmation
   * sheet, the client should invoke the `/submit-form` endpoint with the amount in the same manner
   * as stated above.
   */
  insufficientFundsBlockerAction?: BlockerAction;
  /** Content to display when info button is pressed */
  educationalContent?: PayWithCashAuthorizationBlockerEducationalContent;
  /** The action to perform after scheduled_action_delay_ms. */
  scheduled_action?: BlockerAction;
  /** The amount of time to wait before performing the scheduled_action. */
  scheduled_action_delay_ms?: number;
  /**
   * Indicates which decline prevention view variant should be shown. If not set, falls back to a
   * default variant
   */
  variant?: CashAppPayDeclinePreventionBlockerVariant;
  /** Decorative image to display above decline prevention (variant A) content */
  header_image_element?: FormBlockerElementRemoteImageElement;
}

/** Set of variants used to indicate which decline prevention view variant should be shown */
export enum CashAppPayDeclinePreventionBlockerVariant {
  CONTROL = "CONTROL",
  VARIANT_A = "VARIANT_A",
  VARIANT_C = "VARIANT_C",
}

/** Content needed to render and facilitate business logic for the stepper for selecting amount to top up */
export interface CashAppPayDeclinePreventionBlockerStepperContent {
  /**
   * The amount initially shown. Note that this is also the minimum amount needed for the order
   * to be actually completed. The user may top up an amount lesser than required, but then a
   * decline will occur.
   */
  defaultAmount?: Money;
  /** The max amount the user can select */
  maxAmount?: Money;
  /** The min amount the user can select */
  minAmount?: Money;
  /**
   * The amount to add/subtract from the current amount when the user
   * clicks + (more) or - (less)
   */
  stepAmount?: Money;
  /**
   * The error text shown when the currently-selected amount isn't enough
   * to complete the order, e.g. "Not enough to complete order"
   */
  errorText?: string;
}

export interface PayWithCashAuthorizationBlocker {
  /** The merchant avatar, displayed at the top. */
  avatar?: PayWithCashAuthorizationBlockerAvatar;
  /** Title text, displayed below the avatar. */
  title?: string;
  /** Subtitle text, displayed below the title. */
  subtitle?: string;
  /**
   * Icon, displayed below the subtitle. Deprecated in 3.41.
   *
   * @deprecated
   */
  icon?: PayWithCashAuthorizationBlockerStatusIcon;
  /** Content, displayed below the status icon. */
  content?: PayWithCashAuthorizationBlockerContent;
  /** Footer, displayed at the bottom of the screen. */
  footer?: PayWithCashAuthorizationBlockerFooter;
  /** The action to perform after auto_dismiss_delay_ms. */
  auto_dismiss_action?: BlockerAction;
  /** The amount of time to wait before performing the auto_dismiss_action. */
  auto_dismiss_delay_ms?: number;
  /** The interstitial shown before the main content. */
  on_display_interstitial?: PayWithCashAuthorizationBlockerInterstitial;
  /** The money element. */
  money_element?: FormBlockerElementMoneyElement;
  /** CallToActionElement which could include markdown. */
  call_to_action_element?: FormBlockerElementCallToActionElement;
  /** Section displaying info for in-transaction top-up. */
  in_transaction_top_up_element?: PayWithCashAuthorizationBlockerInTransactionTopUpElement;
  /**
   * The interstitial shown after the main content. Deprecated
   *
   * @deprecated
   */
  on_end_interstitial?: PayWithCashAuthorizationBlockerInterstitial;
}

/**
 * A glyph that may be shown stacked in the lower right corner of the element.
 * Available in client versions >= 3.46.
 */
export enum PayWithCashAuthorizationBlockerBadge {
  /** LINK - Show a link/chain icon */
  LINK = "LINK",
}

export enum PayWithCashAuthorizationBlockerStatusIcon {
  /** SUCCESS - Show a checkmark */
  SUCCESS = "SUCCESS",
  PENDING = "PENDING",
}

export interface PayWithCashAuthorizationBlockerAvatar {
  /** The accent color to use as the background of the avatar if no photo is present. */
  background_color?: Color;
  /** The monogram text to display in the middle of the avatar if no photo is present. */
  monogram_text?: string;
  /** The image to display in the avatar, in place of a background color and monogram. */
  image?: Image1;
  /**
   * A glyph that may be shown stacked in the lower right corner of the element.
   * Available in client versions >= 3.46.
   */
  badge?: PayWithCashAuthorizationBlockerBadge;
  /**
   * The background color to show with the badge, should be set if badge is visible.
   * Available in client versions >= 3.46.
   */
  badge_background_color?: Color;
}

export interface PayWithCashAuthorizationBlockerContent {
  text_content?: PayWithCashAuthorizationBlockerContentTextContent | undefined;
}

export interface PayWithCashAuthorizationBlockerContentTextContent {
  text?: string;
}

export interface PayWithCashAuthorizationBlockerFooter {
  button_footer?: PayWithCashAuthorizationBlockerFooterButtonFooter | undefined;
}

export interface PayWithCashAuthorizationBlockerFooterButtonFooter {
  primary_action?: BlockerAction;
  secondary_action?: BlockerAction;
}

export interface PayWithCashAuthorizationBlockerInterstitial {
  status_interstitial?: PayWithCashAuthorizationBlockerInterstitialStatusInterstitial | undefined;
}

export interface PayWithCashAuthorizationBlockerInterstitialStatusInterstitial {
  icon?: PayWithCashAuthorizationBlockerStatusIcon;
  text?: string;
}

/** Domain data for In-Transaction Top-Up. */
export interface PayWithCashAuthorizationBlockerInTransactionTopUpElement {
  /** Title to display above the ITTU section. */
  title?: string;
  /** Body text for ITTU section. Supports Markdown. */
  body?: string;
  /** Footer text for ITTU section that shows payment instrument information. */
  footer?: string;
  /** Content to display when info button is pressed. */
  educationalContent?: PayWithCashAuthorizationBlockerEducationalContent;
}

/** Content to educate user on adding to cash balance while using Cash App Pay */
export interface PayWithCashAuthorizationBlockerEducationalContent {
  /** Decorative image to display above educational text */
  image_element?: FormBlockerElementRemoteImageElement;
  /** e.g. "Adding to your Cash balance" */
  title?: string;
  /** e.g. "Cash will be added from your linked debit card to complete this order..." */
  body?: string;
  /** e.g. "Got it" */
  dismissButtonText?: string;
  /** e.g. "10% discount applied. Total maximum discount of $10 for purchases up to $100." */
  footer_text?: string;
}

export interface AccountRecoveryMenuBlocker {
}

export interface CampaignSupporterRequest {
  supporter_data?: CampaignSupporterData;
}

export interface CampaignSupporterResponse {
  blockers?: Blockers;
  payment?: Payment;
}

/** Deprecated */
export interface IosDonationBlocker {
}

export interface ConfirmIosDonationRequest {
}

export interface ConfirmIosDonationResponse {
  blockers?: Blockers;
  payment?: Payment;
}

export interface NameRequest {
  /** The display name of the customer. At most 191 characters. */
  full_name?: string;
}

export interface NameResponse {
  blockers?: Blockers;
  payment?: Payment;
}

export interface EmailRequest {
  email?: string;
}

export interface EmailResponse {
  blockers?: Blockers;
  payment?: Payment;
}

export interface EmailVerificationRequest {
  /** The email address to verify. */
  email?: string;
  /** The verification code sent to the email address. */
  verification_code?: string;
}

export interface EmailVerificationResponse {
  blockers?: Blockers;
  payment?: Payment;
}

export interface CreateStoredValueResponse {
  stored_value?: Instrument;
}

export interface CardSummary {
  /** InstrumentType is equivalent to Connect's CardBrand. */
  brand?: InstrumentType;
  /** The last four digits of the card. */
  pan_suffix?: string;
}

export interface Instrument {
  /** A token identifying the instrument. */
  token?: string;
  /** The type of instrument, such as a debit card or bank account. */
  cash_instrument_type?: CashInstrumentType;
  /**
   * The brand of the card, such as Visa or Mastercard. Only populated for card
   * instruments.
   */
  card_brand?: InstrumentType;
  /**
   * A suffix of the instrument that is suitable for display. Such as the last four
   * numbers of a card or the last three numbers of a bank account.
   */
  suffix?: string;
  /** The name of the bank that issued the instrument. */
  bank_name?: string;
  /**
   * The URL of the icon to use when displaying the instrument in a list. The icon might be the
   * logo of a specific bank or a generic icon. At present, these are black and white icons.
   */
  icon_url?: string;
  /**
   * The URL of the icon to use when displaying the instrument on a details page. The icon might
   * be the logo of a specific bank or a generic icon. At present, these are color icons.
   */
  detail_icon_url?: string;
  /** The balance available in the instrument. For now this is only used with Cash Balances. */
  available_balance?: Money;
  /** The version of this instrument for asynchronous updates. */
  version?: number;
  /** The name to display for the instrument. Examples: "Checking 123" and "Debit Card 1234". */
  display_name?: string;
  localizable_display_name?: LocalizableString;
  /** A short variation of the name of the bank that issued the instrument. */
  bank_short_name?: string;
  /** The wallet address in case it is a BTC cash balance instrument. */
  wallet_address?: string;
  /** The symbol of the equity in case it is an equity instrument. */
  symbol?: string;
  /**
   * Determines if the instrument is in the process of being verified.
   * Unverified instruments should not be usable but should be displayed in the client.
   */
  pending_verification?: boolean;
  /**
   * The URL of the icon to use when displaying the instrument selector in a payment flow.
   * The icon might be the logo of a specific bank or a generic icon.
   */
  selection_icon_url?: string;
  /**
   * Determines whether the instrument supports cash in or not. This information is going to be
   * used to construct or to replace TransferInstrumentMap.
   */
  supports_cash_in?: boolean;
  /**
   * Determines whether the instrument supports cash out or not. This information is going to be
   * used to construct or to replace TransferInstrumentMap.
   */
  supports_cash_out?: boolean;
  /**
   * Timestamp representing when InstrumentLink attempt occurred at. This will help to identify
   * customer's oldest linked instrument.
   */
  occurred_at?: number;
}

export interface ClaimData {
  /** Contains claim data corresponding to the initiated payment. */
  claimable_payment?: ClaimablePayment;
}

export interface ClaimablePayment {
  /** The payment token that the initiator received corresponding to a UiPayment. */
  payment_token?: string;
  /** The token to send to the getter, allowing them to claim the payment. */
  claim_token?: string;
  /** The URL for the getter to claim the payment. */
  claim_url?: string;
}

export interface InstrumentSelection {
  /** The token of the instrument to use for a payment. */
  instrument_token?: string;
  /**
   * The fee amount that the user has confirmed will be applied. If the fee ends up being
   * higher than this amount, the server must ask for confirmation or fail the payment.
   */
  accepted_fee_amount?: Money;
}

export interface CardRequest {
  /**
   * The card's account number.
   *
   * Internal-only: if the card number starts with twelve zeroes, it was a masked card number,
   * like 0000000000001234.
   */
  pan?: string;
  /** An expiration date in MMYY format. */
  expiration?: string;
  /** The card's CVV number. */
  security_code?: string;
  /** The postal code of the card's billing address. */
  postal_code?: string;
  /**
   * Internal-only. Deprecated.
   *
   * @deprecated
   */
  abc?: string;
  /**
   * Indicates the type of card that the customer is linking (Debit or credit). If the
   * card ends up not being of the chosen type, the link will fail and the customer will
   * be asked if they want to link the card as the appropriate type, confirming the fees
   * associated with that card. If not provided, the default is DEBIT_CARD.
   */
  instrument_type?: CashInstrumentType;
  /**
   * Client generated external id.
   * Persisted for clients (like Ledger) to associate link attempts outside of Franklin.
   */
  external_id?: string;
  /**
   * The nonce from Square's E-commerce API.
   * https://docs.connect.squareup.com/articles/ecommerce-overview/
   */
  ecommerce_nonce?: string;
}

export interface CardResponse {
  blockers?: Blockers;
  /** Snapchat-only */
  card_token?: string;
  payment?: Payment;
  transfer?: Transfer;
}

/** Internal-only. */
export interface BankAccountRequest {
  /** A 9 digit routing number. */
  routing_number?: string;
  /**
   * 1 or more digits identifying the bank account number. Note that bank account numbers are only
   * loosely validated and it is the caller's responsibility to prevent mistyped account numbers.
   * Applications accepting account numbers in text fields should require the customer to double-key
   * the account number to prevent typos, which can prevent funds from being lost.
   */
  account_number?: string;
  /**
   * Internal-only. Deprecated.
   *
   * @deprecated
   */
  abc?: string;
  /** The postal code of the bank account's address. */
  postal_code?: string;
}

export interface BankAccountResponse {
  blockers?: Blockers;
  payment?: Payment;
  transfer?: Transfer;
}

export interface RatePlanRequest {
  rate_plan?: RatePlan;
  /**
   * Set to true when the customer is locking in their rate plan. Set to false when the customer
   * is advancing to see the details of a rate plan, but hasn't committed to it yet.
   */
  commit?: boolean;
}

export interface RatePlanResponse {
  blockers?: Blockers;
  payment?: Payment;
}

export interface PhoneNumberRequest {
  /**
   * The phone number to link. The number must be a 10-digit number.
   *
   * The sandbox will send real SMS messages. For integration testing in the
   * sandbox, use "555" as the area code like 555-867-5309 to prevent SMS
   * messages from being sent.
   */
  phone_number?: string;
  /**
   * If the number was already verified by the requesting server,
   * this is an HMAC-SHA256 (key is client_secret) of as JSON list (without spaces) including number.
   */
  signature?: string;
}

export interface PhoneNumberResponse {
  blockers?: Blockers;
  payment?: Payment;
}

export interface PhoneVerificationRequest {
  /** The phone number to verify. The number must be a 10-digit number. */
  phone_number?: string;
  /** The verification code sent to the phone. */
  verification_code?: string;
}

export interface PhoneVerificationResponse {
  blockers?: Blockers;
  payment?: Payment;
}

/**
 * Personal data for identity confirmation. Either ssn_last_four or ssn should be sent, but not
 * both. Use ssn_last_four for easier data entry, or ssn to decrease the chance of an identity
 * verification failure.
 */
export interface IdentityVerificationRequest {
  /** The full legal name of the customer. At most 191 characters. */
  full_name?: string;
  /** ISO_8601 format. YYYY-MM-DD. */
  birthdate?: string;
  /** The last four digits of the customer's social security number. */
  ssn_last_four?: string;
  /** The customer's full social security number. */
  ssn?: string;
  /** The customer's address. */
  address?: GlobalAddress;
}

export interface IdentityVerificationResponse {
  blockers?: Blockers;
  payment?: Payment;
  /** If set, this message provides extra information for the user. */
  message?: string;
  transfer?: Transfer;
}

/** A very simple endpoint an authenticated customer can post to accept the terms of service. */
export interface AcceptTermsRequest {
}

export interface AcceptTermsResponse {
  blockers?: Blockers;
  payment?: Payment;
}

export interface RefundPaymentResponse {
  payment?: Payment;
}

export interface CashCustomerProfile {
  /** Customer identifier aka customer token. */
  id?: string;
  display_name?: string;
  cashtag?: string;
  formatted_cashtag?: string;
  /** @deprecated */
  synopsis?: string;
  photo_url?: string;
  theme?: CashCustomerProfileTheme;
  is_verified_account?: boolean;
  rate_plan?: RatePlan;
  payment_button_type?: PaymentButtonType;
  campaign_assets?: CampaignAssets;
  default_currency?: CurrencyCode;
  country_code?: Country;
  /** Render data for drawing an avatar. */
  avatar?: Avatar;
}

export enum CashCustomerProfileTheme {
  DEFAULT = "DEFAULT",
  PROJECT_RED = "PROJECT_RED",
  CAMPAIGN = "CAMPAIGN",
}

export interface CampaignAssets {
  twitter_site_visit_pixel_id?: string;
  twitter_purchase_pixel_id?: string;
  media_card_image_url?: string;
  /** Deprecated. To be deleted. */
  short_campaign_flow?: boolean;
}

export interface ConfirmRequest {
  /**
   * Deprecated. Prefer to pass the payment ID in via the URL path.
   *
   * @deprecated
   */
  payment_id?: string;
  passcode?: string;
  /** A token in lieu of a passcode. */
  passcode_token?: string;
  /** The instrument selection which includes the accepted fee. */
  instrument_selection?: InstrumentSelection;
}

export interface ConfirmResponse {
  blockers?: Blockers;
  payment?: Payment;
  /**
   * A token that can be supplied when initiating a payment to skip passcode confirmation for that
   * payment.
   */
  passcode_token?: string;
  transfer?: Transfer;
}

export interface ErrorResponse {
  /**
   * A human-readable message describing the error. This message is always in
   * English.
   */
  message?: string;
  /**
   * The error type like "bad_request" or "bad_request.invalid_parameter". This
   * value does not change based on the locale of the request.
   */
  type?: string;
  /**
   * The request field name that caused the error, like "refund" or
   * "refund.payment_id". Should correspond to the path parameter name or JSON
   * path.
   */
  field?: string;
  /**
   * A human-readable error message like 'Can't use pre-paid cards' or 'Too many
   * passcode attempts' intended for display to an end user (not a developer).
   * Typically informal, brief (30 characters for en-US) and capitalized with no
   * terminating punctuation.
   *
   * Applications should use this when they lack their own human-readable
   * message for this error's type and field.
   *
   * This message is localized to the request's 'Accept-Language'. If the
   * message is not available in the requested language, a fallback language is
   * used like fr-CA (French Canada) for fr-FR (French France), or en-US
   * (English US) for sv-SE (Swedish Sweden).
   */
  localizable_message?: string;
}

/**
 * Posted to the client's specified HTTP endpoint. Webhook requests are sent as
 * JSON HTTP POSTs when a client's customer or payment is updated in Cash.
 */
export interface WebhookRequest {
  /**
   * Deprecated. Replaced with payment.id.
   *
   * @deprecated
   */
  payment_id?: string;
  /**
   * Deprecated. Replaced with customer.id.
   *
   * @deprecated
   */
  customer_id?: string;
  /**
   * Deprecated. Replaced with payment.sender.
   *
   * @deprecated
   */
  sender?: CashCustomer;
  /**
   * Deprecated. Replaced with payment.recipient.
   *
   * @deprecated
   */
  recipient?: CashCustomer;
  /**
   * Non-null if a payment was updated. Typically this indicates that a payment
   * has changed state, such as when money is captured or paid out. This value
   * is intentionally sparse: only the id, sender, and recipient fields are
   * non-null.
   */
  payment?: Payment;
  /**
   * Non-null if a customer was updated. Typically this indicates that the
   * customer has linked or unlinked a debit card to her account, or resolved
   * a different blocker.
   */
  customer?: CashCustomer;
}

export interface RelatedPaymentsResponse {
  payments?: Payment[];
}

/** Internal-only */
export interface PayoutLatencyRequest {
  /**
   * Optional 6-12 digit BIN (bank identification number) to use instead of the customer's currently
   * linked card or bank account.
   */
  bin?: string;
  /**
   * Optional target date when funds should arrive in the customer's bank account. If present, the
   * response will include a payout_at date when the payment should be created.
   *
   * ISO8601 formatted UTC DateTime.
   */
  target_payout_received_at?: string;
}

/** Internal-only */
export interface PayoutLatencyResponse {
  /** True if funds arrive in the recipient's account synchronously. */
  instant_payout_capable?: boolean;
  /**
   * Date when the payment should be completed in order to arrive in the customer's bank account by
   * the target_payout_received_at time.
   *
   * ISO8601 formatted UTC DateTime.
   */
  payout_at?: string;
}

/**
 * Internal-only
 * A response object after a JSON logout - a new token to be used if page is not refreshed.
 */
export interface LogoutResponse {
  access_token?: string;
}

export interface SuggestedRecipientsResponse {
  suggested_recipients?: CashCustomer[];
}

/** Reward Programs terms and conditions. * */
export interface RewardProgramDetails {
  /** The url path segment for this reward program details page. */
  url_token?: string;
  /** Title shown on the program details page. */
  title?: string;
  /** Browser tab title text. */
  tab_title?: string;
  /** HTML content for the program details. */
  html_content?: string;
  /** When true, the program details are publicly visible. */
  visible?: boolean;
}

export interface HelpCategory {
  headline?: string;
  description?: string;
}

export interface SupportConfig {
  /** Fields for the apps. */
  help_center_url?: string;
  contact_support_url?: string;
  privacy_policy_url?: string;
  trouble_scanning_url?: string;
  terms_of_service_url?: string;
  help_categories?: HelpCategory[];
}

export interface SetAutoReloadPreferenceRequest {
  preference?: AutoReloadPreference;
}

export interface SetAutoReloadPreferenceResponse {
}

export interface AutoReloadPreference {
  stored_value_token?: string;
  enabled?: boolean;
  threshold_cents?: number;
  reload_amount_cents?: number;
}

export interface PostalAddress {
  line1?: string;
  line2?: string;
  city?: string;
  state?: string;
  zip?: string;
}

/** Returns previously filled in tax information if available. */
export interface GetTaxFormPrefillResponse {
  name?: string;
  tin_status?: GetTaxFormPrefillResponseTinStatus;
  postal_address?: GlobalAddress;
  entity?: TaxEntity;
}

/**
 * The customer can update address information without resubmitting their TIN.
 * We don't send the actual TIN as it is sensitive PII.
 */
export enum GetTaxFormPrefillResponseTinStatus {
  MISSING = "MISSING",
  SSN_ON_FILE = "SSN_ON_FILE",
  EIN_ON_FILE = "EIN_ON_FILE",
}

export interface TaxEntity {
  /** Name */
  name?: string;
  /** Tax Identification Number. Can be an SSN or an EIN */
  tin?: string;
  /** Address */
  postal_address?: GlobalAddress;
  business_type?: TaxEntityBusinessType;
  business_type_other?: string;
  exempt_payee_code?: TaxEntityExemptPayeeCode;
  exemption_from_fatca_code?: TaxEntityExemptionFromFATCACode;
  /** For Business Account */
  signature?: string;
  signed_at_ms?: number;
  /** DriveWealth requires first name and last name separately. * */
  first_name?: string;
  last_name?: string;
}

/** For Business Account */
export enum TaxEntityBusinessType {
  INDIVIDUAL_SOLE_PROPRIETOR_OR_SINGLE_MEMBER_LLC = "INDIVIDUAL_SOLE_PROPRIETOR_OR_SINGLE_MEMBER_LLC",
  C_CORPORATION = "C_CORPORATION",
  S_CORPORATION = "S_CORPORATION",
  PARTNERSHIP = "PARTNERSHIP",
  TRUST_ESTATE = "TRUST_ESTATE",
  LIMITED_LIABILITY_COMPANY_C_CORPORATION = "LIMITED_LIABILITY_COMPANY_C_CORPORATION",
  LIMITED_LIABILITY_COMPANY_S_CORPORATION = "LIMITED_LIABILITY_COMPANY_S_CORPORATION",
  LIMITED_LIABILITY_COMPANY_PARTNERSHIP = "LIMITED_LIABILITY_COMPANY_PARTNERSHIP",
  /** OTHER - Requires populating {@link federal_tax_classification_other}. */
  OTHER = "OTHER",
}

/** For Business Account */
export enum TaxEntityExemptPayeeCode {
  EPC_1 = "EPC_1",
  EPC_2 = "EPC_2",
  EPC_3 = "EPC_3",
  EPC_4 = "EPC_4",
  EPC_5 = "EPC_5",
  EPC_6 = "EPC_6",
  EPC_7 = "EPC_7",
  EPC_8 = "EPC_8",
  EPC_9 = "EPC_9",
  EPC_10 = "EPC_10",
  EPC_11 = "EPC_11",
  EPC_12 = "EPC_12",
  EPC_13 = "EPC_13",
}

/** For Business Account */
export enum TaxEntityExemptionFromFATCACode {
  EFFC_A = "EFFC_A",
  EFFC_B = "EFFC_B",
  EFFC_C = "EFFC_C",
  EFFC_D = "EFFC_D",
  EFFC_E = "EFFC_E",
  EFFC_F = "EFFC_F",
  EFFC_G = "EFFC_G",
  EFFC_H = "EFFC_H",
  EFFC_I = "EFFC_I",
  EFFC_J = "EFFC_J",
  EFFC_K = "EFFC_K",
  EFFC_L = "EFFC_L",
  EFFC_M = "EFFC_M",
}

export interface SetTaxInformationRequest {
  entity?: TaxEntity;
  /** @deprecated */
  tax_year?: number;
}

export interface ClearBadgesRequest {
  /** An optional array of payment tokens to clear badges. If omitted, all badges are cleared. */
  payment_tokens?: string[];
}

export interface AddressRequest {
  /** The customer's address (which may include just the postal code). */
  postal_address?: GlobalAddress;
}

export interface AddressResponse {
  blockers?: Blockers;
  payment?: Payment;
  transfer?: Transfer;
}

export interface StatementTransaction {
  type?: StatementTransactionStatementTransactionType;
  /** When this transaction was initiated, represented as milliseconds since 1970-01-01 UTC. */
  initiated_at?: number;
  /** Name of the other customer in the payment, or a string like "Cash Transfer". */
  display_name?: string;
  display_photo_url?: string;
  other_customer_token?: string;
  /** Initiator notes on the payment, or a description like "Cash out" for a transfer. */
  description?: string;
  /** Fee paid by this party for this transaction. */
  fee?: Money;
  /**
   * Positive if this is a push or pull reversal (funds are going to the customer's instrument).
   * Negative if this is a pull or push reversal (funds are coming from the customer's instrument).
   */
  amount?: Money;
  /** Instrument used by this customer for this transaction. */
  instrument?: Instrument;
  /** Stored balance after this transaction completed. */
  balance?: Money;
  /** Whether the the transaction was pending at the time. */
  pending?: boolean;
  /** Context-specific entity token. */
  token?: string;
}

export enum StatementTransactionStatementTransactionType {
  PAYMENT_PULL = "PAYMENT_PULL",
  PAYMENT_PUSH = "PAYMENT_PUSH",
  /** PAYMENT_PUSH_REVERSAL - A pull from a recipient's bank account to undo a push. */
  PAYMENT_PUSH_REVERSAL = "PAYMENT_PUSH_REVERSAL",
  /** PAYMENT_PULL_REVERSAL - A refund or void. */
  PAYMENT_PULL_REVERSAL = "PAYMENT_PULL_REVERSAL",
  /** PAYMENT_PUSH_FAILURE - Transactions where an ACH push has failed asynchronously. */
  PAYMENT_PUSH_FAILURE = "PAYMENT_PUSH_FAILURE",
  TRANSFER_PULL = "TRANSFER_PULL",
  TRANSFER_PUSH = "TRANSFER_PUSH",
  /** TRANSFER_PUSH_REVERSAL - A pull from a recipient's bank account to undo a push. */
  TRANSFER_PUSH_REVERSAL = "TRANSFER_PUSH_REVERSAL",
  /** TRANSFER_PULL_REVERSAL - A refund or void. */
  TRANSFER_PULL_REVERSAL = "TRANSFER_PULL_REVERSAL",
  /** TRANSFER_PUSH_FAILURE - Transactions where an ACH push has failed asynchronously. */
  TRANSFER_PUSH_FAILURE = "TRANSFER_PUSH_FAILURE",
  /** VIRTUAL_CARD_CHARGED - A settled transaction on the virtual card. */
  VIRTUAL_CARD_CHARGED = "VIRTUAL_CARD_CHARGED",
  /** VIRTUAL_CARD_REFUND - A refund was issued or a transaction was reversed. */
  VIRTUAL_CARD_REFUND = "VIRTUAL_CARD_REFUND",
  DIRECT_DEPOSIT = "DIRECT_DEPOSIT",
}

export interface MultifactorChallenge {
  /**
   * Message to display for the challenge. For example, this could be something like
   * "Code sent to (xxx) xxx-1234" for a code challenge, or  "What is your mother's
   * maiden name?" for a question challenge.
   */
  message?: string;
  /** Hint text for answer field. */
  hint_text?: string;
  /** If there are multiple ways to deliver code, lists the alternatives. */
  code_delivery_list?: MultifactorChallengeCodeDeliveryMethod[];
  /** If selection (multiple choice question), lists the possible answers. */
  multiple_choice_answers?: string[];
}

export interface MultifactorChallengeCodeDeliveryMethod {
  /** Masked version of the delivery method e.g. xxx-xxx-5309 for phone. */
  mask?: string;
  /** The type of delivery method ("email", "phone", "card"). */
  type?: string;
}

export interface MultifactorAnswer {
  /** The response to the challenge. */
  response?: string;
}

export interface InstitutionAccount {
  token?: string;
  display_text?: string;
}

export interface StatementRequest {
  /**
   * A timestamp like 2016-01-01T00:00:00Z, represented as milliseconds since 1970-01-01 UTC. The
   * time in this timestamp must 00:00:00 UTC, and the day of month must be 1.
   */
  begin_date?: number;
}

export interface BalanceSnapshot {
  balance?: Money;
}

export interface StatementResponse {
  /**
   * The stored value balance at 00:00:00 on each day, starting on the first of the requested month,
   * and continuing to the first of the next month. For example, for January 2016 this contains 32
   * elements from 2016-01-01 through 2016-02-01.
   */
  balance_snapshots?: BalanceSnapshot[];
  transactions?: StatementTransaction[];
}

export interface TransactionHistoryRequest {
  /**
   * A timestamp like 2016-01-01T00:00:00Z, represented as milliseconds since 1970-01-01 UTC.
   * Must be less than end_date if provided.
   * If not provided, all transactions less than end_date will be provided.
   * If both not provided, all transactions returned.
   */
  begin_date?: number;
  /**
   * A timestamp like 2016-01-01T00:00:00Z, represented as milliseconds since 1970-01-01 UTC.
   * Must be greater than begin_date if provided.
   * If not provided, all transactions after begin_date will be returned.
   * If both not provided, all transactions returned.
   */
  end_date?: number;
  /** Show all transaction history if filter is not present. Otherwise, show only related transactions. */
  filter?: TransactionHistoryRequestTransactionFilter;
  /**
   * Customer token of the dependent of the authenticated customer, validation will be conducted to
   * ensure the authenticated customer is a sponsor of this dependent during the specified time range.
   * If provided, the dependent's transactions will be returned.
   * Otherwise, the authenticated customer's transactions will be returned.
   */
  dependent_customer_token?: string;
}

export enum TransactionHistoryRequestTransactionFilter {
  /** BTC_ONLY - Only return btc transaction history */
  BTC_ONLY = "BTC_ONLY",
  /** EQUITY_ONLY - Only return equity transaction history */
  EQUITY_ONLY = "EQUITY_ONLY",
}

export interface EmptyRequest {
}

export interface EmptyResponse {
}

export interface GetCustomerStatusResponse {
  /** The customer has a linked app token. */
  is_using_cash_app?: boolean;
  /** The customer has ordered a cash card. */
  cash_card_state?: GetCustomerStatusResponseCashCardState;
}

export enum GetCustomerStatusResponseCashCardState {
  /** NO_PHYSICAL_CARD - Customer doesn't have a physical card */
  NO_PHYSICAL_CARD = "NO_PHYSICAL_CARD",
  /** REQUESTED - A physical card has been requested but not shipped. */
  REQUESTED = "REQUESTED",
  /** READY_TO_ACTIVATE - The physical card has been shipped. */
  READY_TO_ACTIVATE = "READY_TO_ACTIVATE",
  /** ACTIVATED - The physical card has been activated. */
  ACTIVATED = "ACTIVATED",
}

export interface CheckBalanceRequest {
  /**
   * Optional timestamp expressed in milliseconds from 1970-01-01T00:00:00Z. If not specified,
   * then the last known balance will be returned.
   */
  timestamp?: number;
}

export interface CheckBalanceResponse {
  instrument?: Instrument;
}

export interface ExchangeTokenRequest {
  client_id?: string;
  client_secret?: string;
  code?: string;
  /** Possibly Unused, adding for backwards compatability. */
  redirect_uri?: string;
}

export interface ExchangeTokenResponse {
  access_token?: string;
  token_type?: string;
  expires_at?: string;
  /** Possibly Unused, adding for backwards compatability. */
  merchant_id?: string;
}

export interface ListPlatformPaymentsRequest {
  no_drafts?: boolean;
  offset?: string;
  begin_time?: string;
  end_time?: string;
  order?: SortOrder;
  limit?: number;
}

/**
 * A blocker used to create a new password, which is longer form and distinct from
 * the PasscodeCreationBlocker, which is used to create a numeric PIN.
 */
export interface PasswordCreationBlocker {
  /** The header text, like 'Create a new password' */
  header_text?: string;
  /**
   * The detail text shown below the header text,
   * like 'Passwords must include at least 8 characters'. Optional.
   */
  detail_text?: string;
  /** The text shown in the password text field when it is empty. */
  password_placeholder?: string;
  /**
   * The help action for displaying a help icon "?" button on the top of the screen.
   * To show multiple help actions in a menu when the "?" button is tapped,
   * use BlockerAction.MenuAction.
   */
  help_action?: BlockerAction;
}

/**
 * A blocker used to verify a customer’s password, which is longer form and distinct from
 * the ‘passcode’ used in the PasscodeVerificationBlocker, which is used to verify a numeric
 * PIN, either for an instrument (CVV) or the overall Cash PIN (tied to the balance).
 */
export interface PasswordVerificationBlocker {
  /** The header text, like 'Enter password' */
  header_text?: string;
  /** The detail text shown below the header text. Optional. */
  detail_text?: string;
  /** The text shown in the password text field when it is empty. */
  password_placeholder?: string;
  /**
   * The help action for displaying a help icon "?" button on the top of the screen.
   * To show multiple help actions in a menu when the "?" button is tapped,
   * use BlockerAction.MenuAction.
   */
  help_action?: BlockerAction;
  /**
   * Whether or not the client should suppress the option to securely store the password
   * token, which is used to allow biometrics to authenticate in lieu of manually entering
   * the password in subsequent instances of this blocker.
   *
   * This only applies when no token is stored on the client, but the client has biometric
   * capabilities.
   */
  should_suppress_password_token_storage?: boolean;
}

/**
 * Note 1: This blocker is purposely defined in this file instead of a separate file.
 * If defined in a separate file, we will encounter a dependency cycle since
 * this blocker depends on image elements defined in this file.
 *
 * Note 2: This blocker is InstantPay name-scoped since it is not re-usable out of the box
 * for future product use cases. For example, future product teams would have to define their
 * own custom polling configuration in the proto. However, the underlying client-side logic is
 * expected to be extendable for a future generic auto-advance blocker.
 */
export interface InstantPayAutoAdvanceBlocker {
  screen?: InstantPayAutoAdvanceBlockerScreen;
  polling_configuration?: InstantPayAutoAdvanceBlockerPollingConfiguration;
}

export interface InstantPayAutoAdvanceBlockerScreen {
  /**
   * backend also provides a remote url for the user's company logo and cash app logo, in that order. The convention
   * is to pass the bottom-right image first (user's company logo)
   * Instant pay won't necessarily utilize all the features of avatar_element, such as badging.
   */
  avatar_element?: FormBlockerElementAvatarElement;
  /** @deprecated */
  local_image_element?: FormBlockerElementLocalImageElement;
  /** @deprecated */
  remote_image_element?: FormBlockerElementRemoteImageElement;
  title?: string;
  subtitle?: string;
  /** @deprecated */
  primary_action?: BlockerAction;
  /** @deprecated */
  secondary_action?: BlockerAction;
  phases?: InstantPayAutoAdvanceBlockerScreenPhase[];
  submit_button_text?: string;
}

export interface InstantPayAutoAdvanceBlockerScreenPhase {
  label?: string;
  sleep?: InstantPayAutoAdvanceBlockerScreenPhaseSleep | undefined;
}

export interface InstantPayAutoAdvanceBlockerScreenPhaseSleep {
  duration_seconds?: number;
}

/** note: mobile side logic hard-codes the path, and is aware of the request and response proto type */
export interface InstantPayAutoAdvanceBlockerPollingConfiguration {
  /** pass this into the polling request */
  payout_attempt_token?: string;
}

/**
 * A blocker that encourages customers to set up Direct Deposit, either through the automated flow
 * or the manual flow.
 *
 * Note: This blocker is purposely defined in this file instead of a separate file. If defined in a
 * separate file, we will encounter a dependency cycle.
 */
export interface DirectDepositSetupBlocker {
  title?: string;
  benefits_button_label?: string;
  benefits_sheet?: DirectDepositSetupBlockerBenefitsSheet;
  automatic_setup_card?: DirectDepositSetupBlockerAutomaticSetupCard;
  manual_setup_card?: DirectDepositSetupBlockerManualSetupCard;
  footer?: string;
  source?: DirectDepositSetupBlockerSource;
}

export enum DirectDepositSetupBlockerSource {
  INSTANT_PAYCHECK = "INSTANT_PAYCHECK",
}

export interface DirectDepositSetupBlockerBenefitsSheet {
  title?: string;
  items?: DirectDepositSetupBlockerBenefitsSheetItem[];
  footerText?: string;
}

export enum DirectDepositSetupBlockerBenefitsSheetIcon {
  CARD = "CARD",
  FDIC = "FDIC",
  LIGHTNING = "LIGHTNING",
  BORROW = "BORROW",
  PHONE = "PHONE",
  ATM = "ATM",
  DEPOSIT = "DEPOSIT",
  OVERDRAFT = "OVERDRAFT",
}

export interface DirectDepositSetupBlockerBenefitsSheetItem {
  icon?: DirectDepositSetupBlockerBenefitsSheetIcon;
  label?: string;
}

export interface DirectDepositSetupBlockerAutomaticSetupCard {
  caption?: string;
  title?: string;
  description?: string;
  button?: FormBlockerElementButtonElement;
}

export interface DirectDepositSetupBlockerManualSetupCard {
  caption?: string;
  title?: string;
  description?: string;
  account_and_routing_data?: DirectDepositSetupBlockerManualSetupCardAccountAndRoutingData;
  button?: FormBlockerElementButtonElement;
}

export interface DirectDepositSetupBlockerManualSetupCardAccountAndRoutingData {
}

/**
 * A blocker allowing users to create or edit a recurring payment. It supports pre-set amount
 * options and a schedule menu that contains various cadences.
 */
export interface RecurringPaymentBlocker {
  text?: RecurringPaymentBlockerText;
  selected_schedule?: RecurringPaymentBlockerSchedule;
  /**
   * If empty, then go directly to numberpad to select amount.
   * Can show up to 6 options (including numberpad)
   */
  amount_options?: RecurringPaymentBlockerAmountOption[];
  cadence_menu?: RecurringPaymentBlockerCadenceMenu;
  /**
   * If both a primary and secondary buttons are specified then the primary button is
   * displayed on the right. If only one is specified then the button is centered.
   */
  primary_button?: RecurringPaymentBlockerButton;
  /**
   * If both a primary and secondary buttons are specified then the secondary button is
   * displayed on the left. If only one is specified then the button is centered.
   */
  secondary_button?: RecurringPaymentBlockerButton;
  /**
   * Receive limit for recurring payment, to be shown if we try to set up a recurring payment
   * larger than this limit. It is communicated in cents.
   */
  receive_limit_per_payment?: number;
  /**
   * Static map of how each Cadence/Schedule combination can be written
   * in the customer's preferred language so that it is readable
   * by customer when going through the process of setting up their recurring payment
   * configuration. The int used to key into the map within each enumerated cadence should
   * be the day_of_cadence selected by the customer
   *
   * {
   * [
   * {
   * cadence: WEEKLY,
   * {
   * 1: "Every Monday",
   * ...
   * }
   * },
   * {
   * cadence: BIWEEKLY,
   * {
   * 1: "Every other Monday",
   * ...
   * }
   * },
   * {
   * cadence: MONTHLY,
   * {
   * 1: "First of the Month",
   * x: "Every x of the Month",
   * 31: "Last of the Month",
   * }
   * }
   * ]
   * }
   */
  enumerated_cadences?: RecurringPaymentBlockerEnumeratedCadence[];
  /** Required minimum amount (in cents) to be sent for recurring payment. */
  receive_minimum_per_payment?: number;
}

export interface RecurringPaymentBlockerText {
  title?: string;
  subtitle?: string;
}

export interface RecurringPaymentBlockerSchedule {
  cadence?: string;
  /**
   * if cadence is weekly or biweekly, should be in [1, 7] such that 1 = Monday ... 7 = Sunday
   * if cadence is monthly, should be in [1, 31]
   */
  day_of_cadence?: number;
  /** e.g. "First day of the month", "Every Friday" */
  description?: string;
}

export interface RecurringPaymentBlockerAmountOption {
  /** The amount is communicated in cents. */
  amount?: number;
  /** set to true to set the default selection. Only one should be set to true. */
  is_selected?: boolean;
  type?: RecurringPaymentBlockerAmountOptionType;
}

export enum RecurringPaymentBlockerAmountOptionType {
  UNKNOWN = "UNKNOWN",
  PRESET = "PRESET",
  /** CUSTOM - Tapping on the CUSTOM option would lead to a numberpad */
  CUSTOM = "CUSTOM",
}

export interface RecurringPaymentBlockerCadenceMenu {
  icon?: FormBlockerElementLocalImageElementIcon;
  text?: RecurringPaymentBlockerText;
  cadence_options?: RecurringPaymentBlockerCadenceMenuCadenceOption[];
}

export interface RecurringPaymentBlockerCadenceMenuCadenceOption {
  button_icon?: Image1;
  button_text?: string;
  day_of_cadence_menu?: RecurringPaymentBlockerCadenceMenuDayOfCadenceMenu;
}

export interface RecurringPaymentBlockerCadenceMenuDayOfCadenceMenu {
  cadence?: string;
  text?: RecurringPaymentBlockerText;
  day_of_cadence_options?: RecurringPaymentBlockerCadenceMenuDayOfCadenceOption[];
}

export interface RecurringPaymentBlockerCadenceMenuDayOfCadenceOption {
  button_icon?: Image1;
  button_text?: string;
  day_of_cadence?: number;
  /**
   * default false, if true:
   * on 3.97 or older clients, it leads to hardcoded atm picker page
   * (text should be hardcoded too)
   * on 3.98+ clients, it leads to a page represented by day_picker
   */
  should_display_day_picker?: boolean;
  day_picker?: RecurringPaymentBlockerCadenceMenuDayOfCadenceMenu;
  button_text_horizontal_alignment?: RecurringPaymentBlockerCadenceMenuDayOfCadenceOptionTextAlignment;
}

export enum RecurringPaymentBlockerCadenceMenuDayOfCadenceOptionTextAlignment {
  UNSPECIFIED = "UNSPECIFIED",
  LEFT = "LEFT",
  CENTER = "CENTER",
  RIGHT = "RIGHT",
}

export interface RecurringPaymentBlockerButton {
  text?: string;
  action?: RecurringPaymentBlockerButtonAction;
}

export enum RecurringPaymentBlockerButtonAction {
  SET = "SET",
  DELETE = "DELETE",
}

export interface RecurringPaymentBlockerEnumeratedCadence {
  cadence?: string;
  enumerated_descriptions?: { [key: number]: string };
}

export interface RecurringPaymentBlockerEnumeratedCadenceEnumeratedDescriptionsEntry {
  key: number;
  value: string;
}
